import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BarChart3,
  CreditCard,
  LayoutDashboard,
  Package,
  Plus,
  RefreshCw,
  Search,
  Ticket,
  Trash2,
  Users,
  Wallet,
  Copy,
  Filter,
  Download,
  Printer,
  FileCode,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Zap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import {
  AppShell,
  PageContainer,
  SectionContainer,
  SearchInput,
  StatCard as UiStatCard,
  StatusBadge,
  UIButton,
} from '../../features/ui-system';
import { Input } from '../../components/ui/input';
import StaffPageHeader from '../../components/StaffPageHeader';
import VoucherTemplateVisualBuilder, { DEFAULT_LAYOUT as DEFAULT_BUILDER_LAYOUT } from '../../components/voucher/VoucherTemplateVisualBuilder.jsx';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const API = `${resolveBackendBaseUrl()}/api`;
const VOUCHER_API = {
  owners: `${API}/voucher/owners`,
  packages: `${API}/voucher/packages`,
  commissions: `${API}/voucher/commissions`,
  batches: `${API}/voucher/batches`,
  generateBatch: `${API}/voucher/batches/generate`,
  reportSummary: `${API}/voucher/reports/summary`,
  templates: `${API}/voucher/templates`,
  templateCards: `${API}/voucher/templates/cards`,
  templateGrouped: `${API}/voucher/templates/grouped`,
  printJobs: `${API}/voucher/print-jobs`,
};

const mapButtonVariant = (variant) => {
  if (variant === 'secondary' || variant === 'outline') return 'subtle';
  if (variant === 'danger') return 'danger';
  if (variant === 'ghost') return 'ghost';
  return 'primary';
};

const mapBadgeVariant = (variant) => {
  if (variant === 'online') return 'ok';
  if (variant === 'offline') return 'danger';
  if (variant === 'warning') return 'warn';
  if (variant === 'info') return 'info';
  if (variant === 'danger') return 'danger';
  return 'neutral';
};

const Card = ({ className = '', children }) => (
  <SectionContainer className={className}>{children}</SectionContainer>
);

const Badge = ({ variant = 'online', children, className = '' }) => (
  <StatusBadge tone={mapBadgeVariant(variant)} className={className}>
    {children}
  </StatusBadge>
);

const KtcButton = ({ variant = 'primary', className = '', ...props }) => (
  <UIButton variant={mapButtonVariant(variant)} className={className} {...props} />
);

const StatCard = ({ label, value, note, trend }) => (
  <UiStatCard title={label} value={value} note={note} trend={trend} tone="info" />
);

const SearchBar = (props) => <SearchInput {...props} />;

/* Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Constants & Defaults Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ */

const emptyOwnerForm = {
  id: '',
  name: '',
  phone: '',
  email: '',
  address: '',
  status: 'active',
  notes: '',
};

const initialGenerateForm = {
  site_id: 'all',
  nas_id: 'all',
  service_type: 'hotspot',
  owner_id: '',
  package_id: '',
  bind_on_login: false,
  login_method: 'same',
  quantity: 10,
  prefix: '',
  char_length: 6,
  code_charset: 'alnum_upper',
};

const VOUCHER_STATUS_MAP = {
  tersedia: { label: 'Tersedia', variant: 'online', icon: CheckCircle },
  available: { label: 'Tersedia', variant: 'online', icon: CheckCircle },
  terjual: { label: 'Terjual', variant: 'warning', icon: Clock },
  sold: { label: 'Terjual', variant: 'warning', icon: Clock },
  terpakai: { label: 'Terpakai', variant: 'info', icon: Zap },
  aktif: { label: 'Aktif', variant: 'info', icon: Zap },
  active: { label: 'Aktif', variant: 'info', icon: Zap },
  expired: { label: 'Kedaluwarsa', variant: 'offline', icon: XCircle },
  habis: { label: 'Habis', variant: 'offline', icon: XCircle },
  used: { label: 'Used', variant: 'offline', icon: XCircle },
  deleted: { label: 'Dihapus', variant: 'danger', icon: Trash2 },
  cancelled: { label: 'Dibatalkan', variant: 'danger', icon: XCircle },
};

/* Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ Tab Definitions Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬ */

const TAB_META = {
  generate: { title: 'Generate Voucher', icon: Package },
  templates: { title: 'Template & Cetak', icon: FileCode },
  vouchers: { title: 'Daftar Voucher', icon: Ticket },
  batches: { title: 'Batch', icon: CreditCard },
  owners: { title: 'Owner / Reseller', icon: Users },
  commissions: { title: 'Komisi Reseller', icon: Wallet },
  topup: { title: 'Topup', icon: CreditCard },
};

const SECTION_META = {
  overview: {
    eyebrow: 'Hotspot',
    title: 'Hotspot Overview',
    subtitle: 'Pusat kendali ringkas voucher hotspot dan reseller.',
    icon: LayoutDashboard,
  },
  voucher: {
    eyebrow: 'Hotspot',
    title: 'Voucher',
    subtitle: 'Kelola generate voucher, daftar voucher, dan batch.',
    icon: Ticket,
  },
  reseller: {
    eyebrow: 'Hotspot',
    title: 'Reseller',
    subtitle: 'Kelola owner/reseller dan komisi reseller.',
    icon: Users,
  },
  analytics: {
    eyebrow: 'Hotspot',
    title: 'Analitik',
    subtitle: 'Analitik ringkas performa voucher hotspot.',
    icon: BarChart3,
  },
};

const HOTSPOT_SECTION_TABS = {
  overview: [],
  voucher: ['generate', 'templates', 'vouchers', 'batches'],
  reseller: ['owners', 'commissions', 'topup'],
  analytics: [],
};

const LEGACY_TAB_TO_SECTION = {
  owners: 'reseller',
  commissions: 'reseller',
  generate: 'voucher',
  vouchers: 'voucher',
  batches: 'voucher',
};

const normalizeSection = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return ['overview', 'voucher', 'reseller', 'analytics'].includes(raw) ? raw : 'overview';
};

const normalizeTab = (value, section = 'overview') => {
  const raw = String(value || '').trim().toLowerCase();
  if (['owner', 'reseller'].includes(raw)) return 'owners';
  const allowedTabs = HOTSPOT_SECTION_TABS[section] || [];
  if (allowedTabs.includes(raw)) return raw;
  return allowedTabs[0] || '';
};

const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  return `Rp ${n.toLocaleString('id-ID')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try { return new Date(dateStr).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '-'; }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try { return new Date(dateStr).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '-'; }
};

const formatDurationMinutes = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return '-';
  const safe = Math.floor(minutes);
  if (safe < 60) return `${safe} menit`;
  const hours = Math.floor(safe / 60);
  const rem = safe % 60;
  if (rem === 0) return `${hours} jam`;
  return `${hours} jam ${rem} menit`;
};

const formatVoucherActiveWindow = (item) => {
  const value = Number(item?.active_value || item?.active_days || item?.active_hours || 0);
  const unitRaw = String(item?.active_unit || '').toLowerCase();
  if (value > 0) {
    if (unitRaw.startsWith('day') || unitRaw === 'hari') return `${value} hari`;
    if (unitRaw.startsWith('hour') || unitRaw === 'jam') return `${value} jam`;
    if (Number(item?.active_days || 0) > 0) return `${Number(item.active_days)} hari`;
    if (Number(item?.active_hours || 0) > 0) return `${Number(item.active_hours)} jam`;
  }
  return formatDurationMinutes(item?.duration_minutes || item?.session_duration_value);
};

const resolveProfileOwnerId = (profile) => {
  const directOwnerId = String(profile?.owner_id || '').trim();
  if (directOwnerId) return directOwnerId;
  const notes = String(profile?.notes || '').trim();
  if (!notes.startsWith('__meta__:')) return '';
  try {
    const parsed = JSON.parse(notes.slice('__meta__:'.length));
    return String(parsed?.owner_id || '').trim();
  } catch {
    return '';
  }
};

const normalizeCommissionStatus = (item) => String(item?.commission_status || item?.status || '').trim().toLowerCase();
const isCommissionPaid = (item) => normalizeCommissionStatus(item) === 'paid';

const normalizeVoucherStatus = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (['available', 'tersedia'].includes(raw)) return 'available';
  if (['active', 'aktif', 'terpakai', 'used'].includes(raw)) return 'active';
  if (['habis'].includes(raw)) return 'used';
  if (['deleted'].includes(raw)) return 'deleted';
  if (['expired', 'kadaluarsa'].includes(raw)) return 'expired';
  if (['sold', 'terjual'].includes(raw)) return 'sold';
  if (['cancelled', 'canceled', 'dibatalkan'].includes(raw)) return 'cancelled';
  return raw || 'available';
};

const resolveVoucherRuntimeStatus = (voucher) => {
  const runtime = String(voucher?.runtime_status || '').trim().toLowerCase();
  if (runtime) return normalizeVoucherStatus(runtime);
  return normalizeVoucherStatus(voucher?.status);
};

const formatSessionRemaining = (voucher) => {
  const prebuilt = String(voucher?.session_remaining_text || '').trim();
  if (prebuilt) return prebuilt;
  const seconds = Number(voucher?.session_remaining_seconds);
  if (!Number.isFinite(seconds)) return '-';
  const safe = Math.max(0, Math.floor(seconds));
  if (safe <= 0) return '0 menit';
  const days = Math.floor(safe / 86400);
  const hours = Math.floor((safe % 86400) / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  if (days > 0) {
    if (hours > 0) return `${days} hari ${hours} jam`;
    return `${days} hari`;
  }
  if (hours > 0) {
    if (minutes > 0) return `${hours} jam ${minutes} menit`;
    return `${hours} jam`;
  }
  return `${minutes} menit`;
};

const isActiveEntity = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  return !normalized || ['active', 'aktif', 'enabled', 'true', '1'].includes(normalized);
};

/* Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â
 * Ã°Å¸Å½Â«  AdminVoucherSystem Ã¢â‚¬â€ Voucher Hotspot & Agen Management
 * Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â */

const AdminVoucherSystem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { section: sectionParam } = useParams();
  const currentSection = normalizeSection(sectionParam);
  const allowedTabs = HOTSPOT_SECTION_TABS[currentSection] || [];
  const searchTab = normalizeTab(new URLSearchParams(location.search).get('tab'), 'voucher');
  const legacySection = LEGACY_TAB_TO_SECTION[searchTab];
  const effectiveSection = sectionParam ? currentSection : (legacySection || currentSection);

  const [activeTab, setActiveTab] = useState(() => normalizeTab(new URLSearchParams(window.location.search).get('tab'), effectiveSection));

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Data state Ã¢â€â‚¬Ã¢â€â‚¬ */
  const [owners, setOwners] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [sites, setSites] = useState([]);
  const [routers, setRouters] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [batches, setBatches] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [financeLedgerEntries, setFinanceLedgerEntries] = useState([]);
  const [voucherTemplates, setVoucherTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [loadingOwners, setLoadingOwners] = useState(false);
  const [loadingVouchers, setLoadingVouchers] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Owner form Ã¢â€â‚¬Ã¢â€â‚¬ */
  const [ownerForm, setOwnerForm] = useState(emptyOwnerForm);
  const [savingOwner, setSavingOwner] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Generate form Ã¢â€â‚¬Ã¢â€â‚¬ */
  const [generateForm, setGenerateForm] = useState(initialGenerateForm);
  const [generating, setGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [lastGeneratedBatch, setLastGeneratedBatch] = useState(null);

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Voucher list Ã¢â€â‚¬Ã¢â€â‚¬ */
  const [voucherSearch, setVoucherSearch] = useState('');
  const [voucherStatusFilter, setVoucherStatusFilter] = useState('all');
  const [voucherOwnerFilter, setVoucherOwnerFilter] = useState('all');
  const [voucherProfileFilter, setVoucherProfileFilter] = useState('all');
  const [voucherSiteFilter, setVoucherSiteFilter] = useState('all');
  const [voucherNasFilter, setVoucherNasFilter] = useState('all');
  const [voucherBatchFilter, setVoucherBatchFilter] = useState('all');
  const [voucherPage, setVoucherPage] = useState(1);
  const [voucherPageSize, setVoucherPageSize] = useState(25);
  const [selectedVoucherIds, setSelectedVoucherIds] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateValidation, setTemplateValidation] = useState(null);
  const [templatePreview, setTemplatePreview] = useState('');
  const [templatePreviewMeta, setTemplatePreviewMeta] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [printDnsHost, setPrintDnsHost] = useState('');
  const [templatePanelMode, setTemplatePanelMode] = useState('print');
  const [showPostGeneratePrintModal, setShowPostGeneratePrintModal] = useState(false);
  const [quickPrintTemplateError, setQuickPrintTemplateError] = useState('');
  const [quickPrintVoucherError, setQuickPrintVoucherError] = useState('');
  const [lastPrintJob, setLastPrintJob] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [processingPrint, setProcessingPrint] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    id: '',
    template_key: '',
    template_name: '',
    category: 'hotspot',
    layout_type: 'grid_3',
    page_size: 'A4',
    page_orientation: 'portrait',
    vouchers_per_page: 3,
    is_active: true,
    is_default: false,
    notes: '',
    builder_mode: false,
    builder_json: DEFAULT_BUILDER_LAYOUT,
    html_content: `<div class="voucher-grid">
  <div class="voucher-card">
    <h3>{{ company_name }}</h3>
    <p>Kode: <strong>{{ voucher_code }}</strong></p>
    <p>User: {{ username }}</p>
    <p>Password: {{ password }}</p>
    <p>Profile: {{ profile_name }}</p>
    <p>Harga: {{ selling_price }}</p>
  </div>
</div>`,
    css_content: `.voucher-card{border:1px solid #cbd5e1;padding:12px;border-radius:12px;font-family:Arial,sans-serif;}`,
  });

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Commission filters Ã¢â€â‚¬Ã¢â€â‚¬ */
  const [commissionOwnerFilter, setCommissionOwnerFilter] = useState('all');
  const [commissionStatusFilter, setCommissionStatusFilter] = useState('all');
  const [commissionDateFrom, setCommissionDateFrom] = useState('');
  const [commissionDateTo, setCommissionDateTo] = useState('');
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
  const [analyticsDateTo, setAnalyticsDateTo] = useState('');
  const [analyticsOwnerFilter, setAnalyticsOwnerFilter] = useState('all');
  const [analyticsSiteFilter, setAnalyticsSiteFilter] = useState('all');
  const [analyticsProfileFilter, setAnalyticsProfileFilter] = useState('all');

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Auth Ã¢â€â‚¬Ã¢â€â‚¬ */
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token') || localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Tab sync Ã¢â€â‚¬Ã¢â€â‚¬ */
  const handleTabChange = (tab) => {
    const safe = normalizeTab(tab, effectiveSection);
    setActiveTab(safe);
    navigate(`/dadu/hotspot/${effectiveSection}?tab=${encodeURIComponent(safe)}`, { replace: true });
  };

  const handleOpenBatchDetail = (batchCode) => {
    setVoucherBatchFilter(String(batchCode || 'all'));
    setVoucherSearch('');
    setVoucherStatusFilter('all');
    setVoucherOwnerFilter('all');
    setVoucherProfileFilter('all');
    setVoucherSiteFilter('all');
    setVoucherNasFilter('all');
    handleTabChange('vouchers');
  };

  useEffect(() => {
    if (!allowedTabs.length) {
      setActiveTab('');
      return;
    }
    const tab = normalizeTab(new URLSearchParams(location.search).get('tab'), effectiveSection);
    setActiveTab(tab);
  }, [location.search, allowedTabs.length, effectiveSection]);

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Data fetchers Ã¢â€â‚¬Ã¢â€â‚¬ */
  const fetchOwners = useCallback(async () => {
    setLoadingOwners(true);
    try {
      const res = await axios.get(VOUCHER_API.owners, { headers: getHeaders() });
      const items = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []);
      setOwners(items);
    } catch { setOwners([]); }
    finally { setLoadingOwners(false); }
  }, [getHeaders]);

  const fetchVouchers = useCallback(async () => {
    setLoadingVouchers(true);
    try {
      const res = await axios.get(`${API}/vouchers`, { headers: getHeaders(), params: { limit: 2000 } });
      const items = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []);
      setVouchers(items);
    } catch { setVouchers([]); }
    finally { setLoadingVouchers(false); }
  }, [getHeaders]);

  const fetchSitesAndRouters = useCallback(async () => {
    try {
      const [sitesRes, routersRes, profilesRes, batchesRes] = await Promise.all([
        axios.get(`${API}/sites`, { headers: getHeaders(), params: { limit: 200 } }).catch(() => ({ data: [] })),
        axios.get(`${API}/routers`, { headers: getHeaders() }).catch(() => ({ data: [] })),
        axios.get(VOUCHER_API.packages, { headers: getHeaders(), params: { service_type: 'hotspot' } }).catch(() => ({ data: [] })),
        axios.get(VOUCHER_API.batches, { headers: getHeaders() }).catch(() => ({ data: [] })),
      ]);
      setSites(Array.isArray(sitesRes.data) ? sitesRes.data : (Array.isArray(sitesRes.data?.items) ? sitesRes.data.items : []));
      setRouters(Array.isArray(routersRes.data) ? routersRes.data : []);
      const rawProfiles = Array.isArray(profilesRes.data) ? profilesRes.data : (Array.isArray(profilesRes.data?.items) ? profilesRes.data.items : []);
      const normalizedProfiles = rawProfiles.map((item) => ({
        ...item,
        id: item?.id,
        name: item?.profile_name || item?.name || '-',
        price: Number(item?.selling_price ?? item?.price ?? 0),
        owner_id: String(item?.owner_id || '').trim() || resolveProfileOwnerId(item) || null,
      }));
      setProfiles(normalizedProfiles);
      setBatches(Array.isArray(batchesRes.data) ? batchesRes.data : (Array.isArray(batchesRes.data?.items) ? batchesRes.data.items : []));
    } catch { /* silent */ }
  }, [getHeaders]);

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await axios.get(VOUCHER_API.commissions, { headers: getHeaders() });
      const items = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []);
      setCommissions(items);
    } catch { setCommissions([]); }
  }, [getHeaders]);

  const fetchVoucherTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(VOUCHER_API.templateCards, {
        headers: getHeaders(),
        params: { category: 'hotspot', is_active: true },
      });
      const items = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []);
      setVoucherTemplates(items);
      if (!selectedTemplateId && items.length > 0) {
        const defaultItem = items.find((item) => item.is_default) || items[0];
        setSelectedTemplateId(String(defaultItem.id));
      }
    } catch {
      setVoucherTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [getHeaders, selectedTemplateId]);

  const fetchFinanceLedger = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const [ledgerRes, reportSummaryRes] = await Promise.all([
        axios.get(`${API}/finance/ledger`, { headers: getHeaders(), params: { limit: 2000 } }).catch(() => ({ data: { items: [] } })),
        axios.get(VOUCHER_API.reportSummary, { headers: getHeaders() }).catch(() => ({ data: {} })),
      ]);
      const ledgerItems = Array.isArray(ledgerRes.data) ? ledgerRes.data : (Array.isArray(ledgerRes.data?.items) ? ledgerRes.data.items : []);
      const summary = reportSummaryRes.data?.summary || null;
      setFinanceLedgerEntries(ledgerItems.length ? ledgerItems : (summary ? [{ source_type: 'voucher_sale_summary_fallback', amount: Number(summary.revenue || 0), entry_type: 'income' }] : []));
    } catch {
      setFinanceLedgerEntries([]);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [getHeaders]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchOwners(),
      fetchVouchers(),
      fetchSitesAndRouters(),
      fetchCommissions(),
      fetchFinanceLedger(),
      fetchVoucherTemplates(),
    ]);
  }, [fetchOwners, fetchVouchers, fetchSitesAndRouters, fetchCommissions, fetchFinanceLedger, fetchVoucherTemplates]);

  useEffect(() => { refreshAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const preparePrintFlowFromGeneratedBatch = async (batchInfo) => {
    const batchCode = String(batchInfo?.batch_code || '').trim();
    const batchId = String(batchInfo?.id || batchInfo?.batch_id || '').trim();
    if (!batchCode && !batchId) return;

    setVoucherSearch('');
    setVoucherStatusFilter('all');
    setVoucherOwnerFilter('all');
    setVoucherProfileFilter('all');
    setVoucherSiteFilter('all');
    setVoucherNasFilter('all');
    setVoucherBatchFilter(batchCode || 'all');
    setVoucherPage(1);

    try {
      const params = { limit: 2000 };
      if (batchId) params.batch_id = batchId;
      const res = await axios.get(`${API}/vouchers`, { headers: getHeaders(), params });
      const items = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []);
      const scoped = batchCode
        ? items.filter((item) => String(item.batch_code || '').trim() === batchCode)
        : items;
      const ids = scoped
        .map((item) => String(item.id || '').trim())
        .filter(Boolean)
        .slice(0, 500);
      setVouchers(items);
      setSelectedVoucherIds(ids);
      toast.success(`Batch ${batchCode || '-'} siap dicetak. Lanjutkan ke popup cetak cepat.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch berhasil dibuat, tapi penyiapan cetak otomatis gagal');
    }
  };

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Owner handlers Ã¢â€â‚¬Ã¢â€â‚¬ */
  const handleSaveOwner = async (event) => {
    event.preventDefault();
    if (!ownerForm.name || !ownerForm.phone) { toast.error('Nama dan nomor HP wajib diisi'); return; }
    const payload = { name: ownerForm.name.trim(), phone: ownerForm.phone.trim(), email: ownerForm.email?.trim() || null, address: ownerForm.address?.trim() || null, status: ownerForm.status || 'active', notes: ownerForm.notes?.trim() || null };
    setSavingOwner(true);
    try {
      if (ownerForm.id) { await axios.put(`${VOUCHER_API.owners}/${ownerForm.id}`, payload, { headers: getHeaders() }); toast.success('Owner berhasil diperbarui'); }
      else { await axios.post(VOUCHER_API.owners, payload, { headers: getHeaders() }); toast.success('Owner berhasil ditambahkan'); }
      setOwnerForm(emptyOwnerForm);
      fetchOwners();
    } catch (error) { toast.error(error.response?.data?.detail || 'Gagal menyimpan owner'); }
    finally { setSavingOwner(false); }
  };

  const handleDeleteOwner = async (ownerId) => {
    if (!window.confirm('Hapus Owner / Reseller ini?')) return;
    try { await axios.delete(`${VOUCHER_API.owners}/${ownerId}`, { headers: getHeaders() }); toast.success('Owner berhasil dihapus'); fetchOwners(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Gagal menghapus owner'); }
  };

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Generate handlers Ã¢â€â‚¬Ã¢â€â‚¬ */
  const handleGenerate = async (event) => {
    event.preventDefault();
    if (!generateForm.owner_id) { toast.error('Owner / reseller wajib dipilih'); return; }
    if (!generateForm.package_id) { toast.error('Hotspot profile wajib dipilih'); return; }
    if (Number(generateForm.quantity || 0) < 1 || Number(generateForm.quantity || 0) > 2000) { toast.error('Jumlah voucher 1-2000'); return; }
    if (Number(generateForm.char_length || 0) < 4 || Number(generateForm.char_length || 0) > 10) { toast.error('Panjang kode wajib 4-10 karakter'); return; }
    const normalizedPrefix = String(generateForm.prefix || '').trim().toUpperCase();
    if (normalizedPrefix && !/^[A-Z0-9-]+$/.test(normalizedPrefix)) { toast.error('Prefix hanya boleh huruf, angka, atau tanda minus (-)'); return; }
    if (!['alnum_upper', 'alnum_lower', 'alpha', 'numeric'].includes(String(generateForm.code_charset || '').toLowerCase())) {
      toast.error('Charset kode tidak valid');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        site_id: generateForm.site_id === 'all' ? null : generateForm.site_id,
        nas_id: generateForm.nas_id === 'all' ? null : generateForm.nas_id,
        owner_id: generateForm.owner_id,
        package_id: generateForm.package_id,
        bind_on_login: Boolean(generateForm.bind_on_login),
        mode: generateForm.login_method,
        login_method: generateForm.login_method,
        quantity: Math.max(1, Math.min(2000, Number(generateForm.quantity || 1))),
        char_length: Math.max(4, Math.min(10, Number(generateForm.char_length || 6))),
        prefix: normalizedPrefix,
        code_charset: generateForm.code_charset,
      };
      const res = await axios.post(VOUCHER_API.generateBatch, payload, { headers: getHeaders() });
      const sample = Array.isArray(res.data?.sample) ? res.data.sample : [];
      const codes = sample.map((item) => item?.code).filter(Boolean);
      const generatedBatch = res.data?.batch || null;
      setGeneratedCodes(codes);
      setLastGeneratedBatch(generatedBatch);
      toast.success(res.data?.message || `${codes.length} voucher berhasil digenerate`);
      refreshAll();
      if (generatedBatch) {
        await preparePrintFlowFromGeneratedBatch(generatedBatch);
        setShowPostGeneratePrintModal(true);
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Gagal generate voucher'); }
    finally { setGenerating(false); }
  };

  const handleCopyAllCodes = async () => {
    if (generatedCodes.length === 0) { toast.error('Belum ada kode voucher'); return; }
    const text = generatedCodes.map((v) => typeof v === 'string' ? v : v.code).join('\n');
    try { await navigator.clipboard.writeText(text); toast.success('Semua kode berhasil disalin'); }
    catch { toast.error('Gagal menyalin'); }
  };

  const handleToggleVoucherSelect = (voucherId) => {
    const id = String(voucherId || '').trim();
    if (!id) return;
    setSelectedVoucherIds((prev) => (
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    ));
  };

  const mergeSelectedVoucherIds = (ids) => {
    const normalized = ids.map((item) => String(item || '').trim()).filter(Boolean);
    setSelectedVoucherIds((prev) => {
      const merged = new Set([...(prev || []), ...normalized]);
      return Array.from(merged);
    });
  };

  const handleSelectFilteredVouchers = () => {
    const ids = filteredVouchers.slice(0, 300).map((item) => String(item.id || '')).filter(Boolean);
    mergeSelectedVoucherIds(ids);
  };

  const handleSelectCurrentPageVouchers = () => {
    const ids = paginatedVouchers.slice(0, 300).map((item) => String(item.id || '')).filter(Boolean);
    mergeSelectedVoucherIds(ids);
  };

  const handleClearVoucherSelection = () => {
    setSelectedVoucherIds([]);
  };

  const handleSelectCurrentBatchForPrint = () => {
    if (voucherBatchFilter === 'all') {
      toast.error('Pilih batch dulu');
      return;
    }
    const ids = filteredVouchers
      .filter((item) => String(item.batch_code || '') === String(voucherBatchFilter))
      .slice(0, 300)
      .map((item) => String(item.id || '').trim())
      .filter(Boolean);
    mergeSelectedVoucherIds(ids);
    toast.success(`${ids.length} voucher dipilih dari batch ${voucherBatchFilter}`);
  };

  const handleBulkDisableVouchers = async () => {
    if (selectedVoucherIds.length === 0) {
      toast.error('Pilih voucher dulu');
      return;
    }
    if (!window.confirm(`Disable ${selectedVoucherIds.length} voucher terpilih?`)) return;
    try {
      const res = await axios.post(`${API}/vouchers/bulk-action`, {
        action: 'disable',
        voucher_ids: selectedVoucherIds,
      }, { headers: getHeaders() });
      toast.success(res.data?.message || 'Voucher terpilih berhasil di-disable');
      await fetchVouchers();
      setSelectedVoucherIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disable voucher terpilih');
    }
  };

  const handleBulkDeleteVouchers = async () => {
    if (selectedVoucherIds.length === 0) {
      toast.error('Pilih voucher dulu');
      return;
    }
    if (!window.confirm(`Hapus ${selectedVoucherIds.length} voucher terpilih? Aksi ini permanen.`)) return;
    try {
      const res = await axios.post(`${API}/vouchers/bulk-action`, {
        action: 'delete',
        voucher_ids: selectedVoucherIds,
      }, { headers: getHeaders() });
      toast.success(res.data?.message || 'Voucher terpilih berhasil dihapus');
      await fetchVouchers();
      setSelectedVoucherIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus voucher terpilih');
    }
  };

  const handleDisconnectVoucherSession = async (voucher) => {
    const voucherId = String(voucher?.id || '').trim();
    if (!voucherId) {
      toast.error('Voucher ID tidak valid');
      return;
    }
    const username = String(voucher?.username || voucher?.code || '-').trim();
    const confirmed = window.confirm(`Disconnect sesi hotspot untuk voucher ${username}?`);
    if (!confirmed) return;
    try {
      const res = await axios.post(
        `${API}/vouchers/${voucherId}/disconnect-hotspot-sessions`,
        {},
        { headers: getHeaders() },
      );
      const disconnected = Number(res.data?.result?.sessions_disconnected || 0);
      if (disconnected > 0) {
        toast.success(`Berhasil disconnect ${disconnected} sesi aktif`);
      } else {
        toast.message('Tidak ada sesi aktif yang perlu diputus');
      }
      await fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disconnect sesi voucher');
    }
  };

  const handleLoadTemplateToEditor = async (templateId) => {
    try {
      const res = await axios.get(`${VOUCHER_API.templates}/${templateId}`, { headers: getHeaders() });
      const row = res.data || {};
      setTemplateForm({
        id: String(row.id || ''),
        template_key: row.template_key || '',
        template_name: row.template_name || '',
        category: row.category || 'hotspot',
        layout_type: row.layout_type || 'grid_3',
        page_size: row.page_size || 'A4',
        page_orientation: row.page_orientation || 'portrait',
        vouchers_per_page: Number(row.vouchers_per_page || 3),
        is_active: Boolean(row.is_active),
        is_default: Boolean(row.is_default),
        notes: row.notes || '',
        builder_mode: Boolean(row.builder_mode),
        builder_json: row.builder_json || DEFAULT_BUILDER_LAYOUT,
        html_content: row.html_content || '',
        css_content: row.css_content || '',
      });
      setTemplateValidation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat template');
    }
  };

  const handleSaveTemplate = async (event) => {
    event.preventDefault();
    if (!templateForm.template_name.trim()) {
      toast.error('Nama template wajib diisi');
      return;
    }
    if (!templateForm.template_key.trim() && !templateForm.id) {
      toast.error('Template key wajib diisi');
      return;
    }
    if (templateForm.builder_mode && !templateForm.builder_json) {
      toast.error('Builder JSON wajib diisi untuk Visual Builder');
      return;
    }
    if (!templateForm.builder_mode && !templateForm.html_content.trim()) {
      toast.error('HTML content wajib diisi pada mode Advanced');
      return;
    }
    const payload = {
      template_key: templateForm.template_key.trim(),
      template_name: templateForm.template_name.trim(),
      category: templateForm.category,
      layout_type: templateForm.layout_type,
      builder_mode: Boolean(templateForm.builder_mode),
      builder_json: templateForm.builder_mode ? (templateForm.builder_json || DEFAULT_BUILDER_LAYOUT) : null,
      html_content: templateForm.html_content,
      css_content: templateForm.css_content || null,
      page_size: templateForm.page_size,
      page_orientation: templateForm.page_orientation,
      vouchers_per_page: Number(templateForm.vouchers_per_page || 3),
      is_active: Boolean(templateForm.is_active),
      is_default: Boolean(templateForm.is_default),
      notes: templateForm.notes?.trim() || null,
    };
    setSavingTemplate(true);
    try {
      const request = templateForm.id
        ? axios.put(`${VOUCHER_API.templates}/${templateForm.id}`, payload, { headers: getHeaders() })
        : axios.post(VOUCHER_API.templates, payload, { headers: getHeaders() });
      const res = await request;
      const savedId = String(res.data?.id || '');
      if (payload.is_default && savedId) {
        await axios.post(`${VOUCHER_API.templates}/${savedId}/set-default`, {}, { headers: getHeaders() });
      }
      toast.success(templateForm.id ? 'Template berhasil diperbarui' : 'Template berhasil ditambahkan');
      await fetchVoucherTemplates();
      if (savedId) {
        setSelectedTemplateId(savedId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleValidateTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error('Pilih template dulu');
      return;
    }
    try {
      const validatePayload = templateForm.builder_mode
        ? { builder_json: templateForm.builder_json || DEFAULT_BUILDER_LAYOUT }
        : { html_content: templateForm.html_content || '' };
      const res = await axios.post(
        `${VOUCHER_API.templates}/${selectedTemplateId}/validate`,
        validatePayload,
        { headers: getHeaders() },
      );
      setTemplateValidation(res.data || null);
      if (res.data?.valid) toast.success('Validasi template sukses');
      else toast.error('Template memiliki variabel yang tidak valid');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal validasi template');
    }
  };

  const handlePreviewTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error('Pilih template dulu');
      return;
    }
    if (selectedVoucherIds.length === 0) {
      toast.error('Pilih minimal satu voucher untuk preview');
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const payload = {
        voucher_ids: selectedVoucherIds,
      };
      const res = await axios.post(`${API}/voucher/templates/${selectedTemplateId}/preview-print-layout`, payload, { headers: getHeaders() });
      setTemplatePreview(res.data?.html_preview || '');
      setTemplatePreviewMeta(res.data || null);
      toast.success('Preview template siap');
    } catch (error) {
      const message = error.response?.data?.detail || 'Gagal membuat preview template';
      setPreviewError(message);
      toast.error(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openRenderedPrintWindow = useCallback((renderedSnapshot, context = {}) => {
    const html = String(renderedSnapshot || '').trim();
    if (!html) return false;
    const popup = window.open('', `_voucher_print_${Date.now()}`);
    if (!popup) return false;
    const title = `Voucher Print Preview${context?.jobId ? ` #${context.jobId}` : ''}`;
    const dnsText = String(context?.dnsHost || '').trim();
    popup.document.open();
    popup.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    body{margin:0;background:#0b1220;font-family:Arial,sans-serif;color:#e2e8f0}
    .toolbar{position:sticky;top:0;z-index:50;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 14px;background:#020617;border-bottom:1px solid #334155}
    .toolbar .meta{font-size:12px;color:#93c5fd}
    .toolbar .actions{display:flex;gap:8px}
    .toolbar button{border:1px solid #334155;background:#0f172a;color:#e2e8f0;padding:8px 12px;border-radius:8px;cursor:pointer}
    .toolbar button.primary{border-color:#22d3ee;background:#164e63}
    .print-shell{padding:12px;background:#fff}
    @media print{
      body{background:#fff}
      .toolbar{display:none}
      .print-shell{padding:0}
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div>
      <div><strong>${title}</strong></div>
      <div class="meta">Voucher: ${Number(context?.voucherCount || 0)}${dnsText ? ` � DNS: ${dnsText}` : ''}</div>
    </div>
    <div class="actions">
      <button type="button" class="primary" onclick="window.print()">CLICK TO PRINT</button>
      <button type="button" onclick="window.close()">CLOSE</button>
    </div>
  </div>
  <div class="print-shell">${html}</div>
</body>
</html>`);
    popup.document.close();
    return true;
  }, []);

  const handleCreatePrintJob = async ({ openWindow = true } = {}) => {
    if (!selectedTemplateId) {
      toast.error('Pilih template dulu');
      return false;
    }
    if (selectedVoucherIds.length === 0) {
      toast.error('Pilih minimal satu voucher dari daftar');
      return false;
    }
    setProcessingPrint(true);
    try {
      const createRes = await axios.post(
        VOUCHER_API.printJobs,
        {
          template_id: Number(selectedTemplateId),
          voucher_ids: selectedVoucherIds,
          source_type: 'manual_selection',
        },
        { headers: getHeaders() },
      );
      const jobId = createRes.data?.id;
      const renderRes = await axios.post(`${VOUCHER_API.printJobs}/${jobId}/render`, {}, { headers: getHeaders() });
      const renderedSnapshot = String(renderRes.data?.rendered_snapshot || '');
      setLastPrintJob({
        ...(createRes.data || {}),
        ...(renderRes.data || {}),
      });
      setTemplatePreview(renderedSnapshot);
      setTemplatePreviewMeta({
        template_id: renderRes.data?.template_id,
        voucher_count: renderRes.data?.voucher_count || renderRes.data?.total_vouchers || 0,
        page_count: renderRes.data?.page_count || 0,
        layout_type: renderRes.data?.layout_type || templatePreviewMeta?.layout_type || '-',
        page_size: renderRes.data?.page_size || templatePreviewMeta?.page_size || '-',
        orientation: renderRes.data?.orientation || templatePreviewMeta?.orientation || '-',
      });
      if (openWindow) {
        const opened = openRenderedPrintWindow(renderedSnapshot, {
          jobId,
          voucherCount: selectedVoucherIds.length,
          dnsHost: printDnsHost,
        });
        if (!opened) {
          toast.error('Popup preview diblok browser. Izinkan popup lalu coba lagi.');
        }
      }
      toast.success('Print job berhasil dibuat dan siap dicetak');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat print job');
      return false;
    } finally {
      setProcessingPrint(false);
    }
  };

  const handleContinueToPrint = async () => {
    return handleCreatePrintJob({ openWindow: true });
  };

  const handleMoveToPrintFlow = () => {
    if (selectedVoucherIds.length === 0) {
      toast.error('Pilih minimal satu voucher dulu');
      return;
    }
    handleTabChange('templates');
    toast.success('Lanjut ke Step Cetak: pilih template lalu klik Lanjutkan Cetak');
  };

  const handleQuickPrintFromGenerate = async () => {
    if (selectedVoucherIds.length === 0) {
      setQuickPrintVoucherError('Belum ada voucher terpilih untuk dicetak.');
      return;
    }
    if (!selectedTemplateId) {
      setQuickPrintTemplateError('Template voucher wajib dipilih sebelum cetak.');
      return;
    }
    const ok = await handleContinueToPrint();
    if (ok) {
      setQuickPrintTemplateError('');
      setQuickPrintVoucherError('');
      setShowPostGeneratePrintModal(false);
      handleTabChange('templates');
    }
  };

  const handleMarkPrinted = async () => {
    if (!lastPrintJob?.job_id && !lastPrintJob?.id) return;
    const jobId = lastPrintJob.job_id || lastPrintJob.id;
    try {
      await axios.post(`${VOUCHER_API.printJobs}/${jobId}/mark-printed`, {}, { headers: getHeaders() });
      toast.success('Print job ditandai sudah dicetak');
      setLastPrintJob((prev) => ({ ...prev, status: 'printed' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update status print');
    }
  };

  const handleExportPrintPdf = async () => {
    if (!lastPrintJob?.job_id && !lastPrintJob?.id) return;
    const jobId = lastPrintJob.job_id || lastPrintJob.id;
    try {
      const res = await axios.post(`${VOUCHER_API.printJobs}/${jobId}/export-pdf`, {}, { headers: getHeaders() });
      toast.success(res.data?.message || 'Ekspor PDF diproses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengekspor PDF');
    }
  };

  /* Ã¢â€â‚¬Ã¢â€â‚¬ Computed data Ã¢â€â‚¬Ã¢â€â‚¬ */
  const filteredOwners = useMemo(() => {
    const keyword = ownerSearch.trim().toLowerCase();
    return owners.filter((o) => !keyword || [o.name, o.phone, o.email, o.address].filter(Boolean).join(' ').toLowerCase().includes(keyword));
  }, [owners, ownerSearch]);

  const ownerById = useMemo(() => Object.fromEntries(owners.map((o) => [o.id, o])), [owners]);
  const profileById = useMemo(() => Object.fromEntries(profiles.map((p) => [p.id, p])), [profiles]);
  const activeOwners = useMemo(() => owners.filter((item) => isActiveEntity(item?.status)), [owners]);
  const activeProfiles = useMemo(() => profiles.filter((item) => isActiveEntity(item?.status)), [profiles]);
  const siteById = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const routerById = useMemo(() => Object.fromEntries(routers.map((r) => [r.id, r])), [routers]);
  const selectedProfile = useMemo(
    () => profiles.find((item) => String(item.id || '') === String(generateForm.package_id || '')) || null,
    [profiles, generateForm.package_id]
  );
  const profilesByOwner = useMemo(() => {
    if (!generateForm.owner_id) return [];
    return activeProfiles.filter((item) => {
      const profileOwnerId = String(item.owner_id || '').trim();
      const selectedOwnerId = String(generateForm.owner_id || '').trim();
      return !profileOwnerId || profileOwnerId === selectedOwnerId;
    });
  }, [activeProfiles, generateForm.owner_id]);
  const scopedRouters = useMemo(() => {
    if (!generateForm.site_id || generateForm.site_id === 'all') return routers;
    return routers.filter((item) => String(item.site_id || '') === String(generateForm.site_id));
  }, [routers, generateForm.site_id]);
  const generationScope = useMemo(() => {
    if (generateForm.nas_id && generateForm.nas_id !== 'all') return 'NAS Spesifik';
    if (generateForm.site_id && generateForm.site_id !== 'all') return 'Site-wide (semua NAS di site)';
    return 'Global (semua site + semua NAS)';
  }, [generateForm.nas_id, generateForm.site_id]);
  const codePreview = useMemo(() => `${String(generateForm.prefix || '').toUpperCase()}${'X'.repeat(Math.max(4, Math.min(10, Number(generateForm.char_length || 6))))}`, [generateForm.prefix, generateForm.char_length]);

  useEffect(() => {
    if (!generateForm.owner_id) return;
    const ownerStillActive = activeOwners.some((item) => String(item.id || '') === String(generateForm.owner_id || ''));
    if (!ownerStillActive) {
      setGenerateForm((prev) => ({ ...prev, owner_id: '', package_id: '' }));
    }
  }, [activeOwners, generateForm.owner_id]);

  useEffect(() => {
    if (!generateForm.package_id) return;
    const selectedStillValid = profilesByOwner.some((item) => String(item.id || '') === String(generateForm.package_id || ''));
    if (!selectedStillValid) {
      setGenerateForm((prev) => ({ ...prev, package_id: '' }));
    }
  }, [profilesByOwner, generateForm.package_id]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    handleLoadTemplateToEditor(selectedTemplateId);
  }, [selectedTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showPostGeneratePrintModal) return;
    if (selectedTemplateId) return;
    if (!voucherTemplates.length) return;
    const preferred = voucherTemplates.find((tpl) => tpl?.is_default)
      || voucherTemplates.find((tpl) => tpl?.is_active)
      || voucherTemplates[0];
    const preferredId = String(preferred?.id || '').trim();
    if (!preferredId) return;
    setSelectedTemplateId(preferredId);
  }, [showPostGeneratePrintModal, selectedTemplateId, voucherTemplates]);

  useEffect(() => {
    setVoucherPage(1);
  }, [
    voucherSearch,
    voucherStatusFilter,
    voucherOwnerFilter,
    voucherProfileFilter,
    voucherSiteFilter,
    voucherNasFilter,
    voucherBatchFilter,
    voucherPageSize,
  ]);

  const filteredVouchers = useMemo(() => {
    let list = vouchers;
    if (voucherStatusFilter !== 'all') {
      const statusAlias = {
        available: ['available', 'tersedia'],
        sold: ['sold', 'terjual'],
        active: ['active', 'aktif', 'terpakai'],
        used: ['used', 'habis'],
        expired: ['expired'],
        deleted: ['deleted'],
        cancelled: ['cancelled', 'canceled'],
      };
      const validStatuses = statusAlias[voucherStatusFilter] || [voucherStatusFilter];
      list = list.filter((v) => validStatuses.includes(String(v.runtime_status || v.status || '').toLowerCase()));
    }
    if (voucherOwnerFilter !== 'all') list = list.filter((v) => String(v.owner_id || '') === voucherOwnerFilter);
    if (voucherProfileFilter !== 'all') {
      list = list.filter((v) => String(v.profile_id || v.profile_name || '') === voucherProfileFilter);
    }
    if (voucherSiteFilter !== 'all') list = list.filter((v) => String(v.site_id || '') === voucherSiteFilter);
    if (voucherNasFilter !== 'all') list = list.filter((v) => String(v.nas_id || '') === voucherNasFilter);
    if (voucherBatchFilter !== 'all') list = list.filter((v) => String(v.batch_code || '') === voucherBatchFilter);
    const keyword = voucherSearch.trim().toLowerCase();
    if (keyword) list = list.filter((v) => [v.code, v.profile_name, v.owner_name].filter(Boolean).join(' ').toLowerCase().includes(keyword));
    return list;
  }, [vouchers, voucherStatusFilter, voucherOwnerFilter, voucherProfileFilter, voucherSiteFilter, voucherNasFilter, voucherBatchFilter, voucherSearch]);

  const voucherTotalPages = useMemo(() => {
    const perPage = Math.max(1, Number(voucherPageSize || 25));
    return Math.max(1, Math.ceil(filteredVouchers.length / perPage));
  }, [filteredVouchers.length, voucherPageSize]);

  const safeVoucherPage = useMemo(() => {
    const page = Math.max(1, Number(voucherPage || 1));
    return Math.min(page, voucherTotalPages);
  }, [voucherPage, voucherTotalPages]);

  const paginatedVouchers = useMemo(() => {
    const perPage = Math.max(1, Number(voucherPageSize || 25));
    const start = (safeVoucherPage - 1) * perPage;
    return filteredVouchers.slice(start, start + perPage);
  }, [filteredVouchers, safeVoucherPage, voucherPageSize]);

  const profileFilterOptions = useMemo(() => {
    const directProfiles = profiles.map((item) => ({
      value: String(item.id || ''),
      label: item.name || String(item.id || ''),
    })).filter((item) => item.value);
    if (directProfiles.length > 0) return directProfiles;
    const dynamic = new Map();
    for (const voucher of vouchers) {
      const value = String(voucher.profile_id || voucher.profile_name || '').trim();
      if (!value || dynamic.has(value)) continue;
      dynamic.set(value, voucher.profile_name || value);
    }
    return Array.from(dynamic.entries()).map(([value, label]) => ({ value, label }));
  }, [profiles, vouchers]);

  const batchStatsByCode = useMemo(() => {
    const summary = {};
    for (const voucher of vouchers) {
      const batchCode = String(voucher.batch_code || '').trim();
      if (!batchCode) continue;
      if (!summary[batchCode]) {
        summary[batchCode] = { generated: 0, active: 0, expired: 0, available: 0 };
      }
      const status = normalizeVoucherStatus(voucher.status);
      summary[batchCode].generated += 1;
      if (status === 'active') summary[batchCode].active += 1;
      if (status === 'expired') summary[batchCode].expired += 1;
      if (status === 'available') summary[batchCode].available += 1;
    }
    return summary;
  }, [vouchers]);

  const filteredCommissions = useMemo(() => {
    let list = commissions;
    if (commissionOwnerFilter !== 'all') list = list.filter((c) => String(c.owner_id) === commissionOwnerFilter);
    if (commissionStatusFilter !== 'all') list = list.filter((c) => normalizeCommissionStatus(c) === commissionStatusFilter);
    if (commissionDateFrom) {
      list = list.filter((c) => {
        const baseDate = String(c.activated_at || c.created_at || '').slice(0, 10);
        return baseDate && baseDate >= commissionDateFrom;
      });
    }
    if (commissionDateTo) {
      list = list.filter((c) => {
        const baseDate = String(c.activated_at || c.created_at || '').slice(0, 10);
        return baseDate && baseDate <= commissionDateTo;
      });
    }
    return list;
  }, [commissions, commissionOwnerFilter, commissionStatusFilter, commissionDateFrom, commissionDateTo]);
  const quickPrintReady = selectedVoucherIds.length > 0 && !!String(selectedTemplateId || '').trim();
  const quickPrintMissingItems = useMemo(() => {
    const missing = [];
    if (selectedVoucherIds.length === 0) missing.push('Pilih minimal 1 voucher');
    if (!String(selectedTemplateId || '').trim()) missing.push('Pilih template voucher');
    return missing;
  }, [selectedVoucherIds.length, selectedTemplateId]);

  const ownerSummary = useMemo(() => {
    const summaryByOwner = {};
    for (const owner of owners) {
      summaryByOwner[owner.id] = {
        owner_id: owner.id,
        vouchers: 0,
        active_vouchers: 0,
        commission_pending: 0,
        commission_paid: 0,
      };
    }
    for (const voucher of vouchers) {
      const ownerId = String(voucher.owner_id || '').trim();
      if (!ownerId) continue;
      if (!summaryByOwner[ownerId]) {
        summaryByOwner[ownerId] = { owner_id: ownerId, vouchers: 0, active_vouchers: 0, commission_pending: 0, commission_paid: 0 };
      }
      summaryByOwner[ownerId].vouchers += 1;
      if (normalizeVoucherStatus(voucher.status) === 'active') summaryByOwner[ownerId].active_vouchers += 1;
    }
    for (const commission of commissions) {
      const ownerId = String(commission.owner_id || '').trim();
      if (!ownerId) continue;
      if (!summaryByOwner[ownerId]) {
        summaryByOwner[ownerId] = { owner_id: ownerId, vouchers: 0, active_vouchers: 0, commission_pending: 0, commission_paid: 0 };
      }
      const amount = Number(commission.reseller_fee || commission.amount || commission.commission_amount || 0);
      if (isCommissionPaid(commission)) summaryByOwner[ownerId].commission_paid += amount;
      else summaryByOwner[ownerId].commission_pending += amount;
    }
    return summaryByOwner;
  }, [owners, vouchers, commissions]);

  const resellerSummary = useMemo(() => {
    const totalVoucher = vouchers.length;
    const activeVoucher = vouchers.filter((item) => normalizeVoucherStatus(item.status) === 'active').length;
    const totalPayable = commissions
      .filter((item) => !isCommissionPaid(item))
      .reduce((sum, item) => sum + Number(item.reseller_fee || item.amount || item.commission_amount || 0), 0);
    const totalPaid = commissions
      .filter((item) => isCommissionPaid(item))
      .reduce((sum, item) => sum + Number(item.reseller_fee || item.amount || item.commission_amount || 0), 0);
    const pendingPayoutCount = commissions.filter((item) => !isCommissionPaid(item)).length;
    return { totalVoucher, activeVoucher, totalPayable, totalPaid, pendingPayoutCount };
  }, [vouchers, commissions]);

  const voucherStats = useMemo(() => {
    const total = vouchers.length;
    const tersedia = vouchers.filter((v) => normalizeVoucherStatus(v.status) === 'available').length;
    const terjual = vouchers.filter((v) => normalizeVoucherStatus(v.status) === 'sold').length;
    const totalRevenue = vouchers
      .filter((v) => ['sold', 'active'].includes(normalizeVoucherStatus(v.status)))
      .reduce((sum, v) => sum + Number(v.price || 0), 0);
    return { total, tersedia, terjual, totalRevenue };
  }, [vouchers]);

  const overviewSummary = useMemo(() => {
    const todayKey = new Date().toISOString().slice(0, 10);
    const total = vouchers.length;
    const active = vouchers.filter((item) => normalizeVoucherStatus(item.status) === 'active').length;
    const expired = vouchers.filter((item) => normalizeVoucherStatus(item.status) === 'expired').length;
    const todayRevenue = vouchers
      .filter((item) => ['sold', 'active'].includes(normalizeVoucherStatus(item.status)))
      .filter((item) => String(item.activated_at || item.updated_at || item.created_at || '').slice(0, 10) === todayKey)
      .reduce((sum, item) => sum + Number(item.price || 0), 0);
    const pendingCommission = commissions
      .filter((item) => !isCommissionPaid(item))
      .reduce((sum, item) => sum + Number(item.reseller_fee || item.amount || item.commission_amount || 0), 0);
    const activeResellers = owners.filter((owner) => String(owner.status || '').toLowerCase() === 'active').length;
    return { total, active, expired, todayRevenue, pendingCommission, activeResellers };
  }, [vouchers, commissions, owners]);

  const overviewInsights = useMemo(() => {
    const byOwner = {};
    const byProfile = {};
    for (const voucher of vouchers) {
      const ownerId = String(voucher.owner_id || '').trim();
      if (ownerId) byOwner[ownerId] = (byOwner[ownerId] || 0) + 1;
      const profileKey = String(voucher.profile_name || voucher.profile_id || '').trim();
      if (profileKey) byProfile[profileKey] = (byProfile[profileKey] || 0) + 1;
    }
    const topOwnerEntry = Object.entries(byOwner).sort((a, b) => b[1] - a[1])[0];
    const topProfileEntry = Object.entries(byProfile).sort((a, b) => b[1] - a[1])[0];
    const total = Math.max(1, vouchers.length);
    const expiredRatio = vouchers.filter((item) => normalizeVoucherStatus(item.status) === 'expired').length / total;
    const activeRatio = vouchers.filter((item) => normalizeVoucherStatus(item.status) === 'active').length / total;
    const warning = expiredRatio >= 0.35
      ? `Voucher expired cukup tinggi (${Math.round(expiredRatio * 100)}%). Cek ulang durasi atau masa aktif profile populer.`
      : (vouchers.length >= 20 && activeRatio < 0.2
        ? 'Konversi aktivasi masih rendah. Tinjau channel distribusi voucher dan kualitas promo.'
        : '');
    return {
      topResellerName: topOwnerEntry ? (ownerById[topOwnerEntry[0]]?.name || topOwnerEntry[0]) : '',
      topResellerCount: topOwnerEntry ? topOwnerEntry[1] : 0,
      topProfileName: topProfileEntry ? topProfileEntry[0] : '',
      topProfileCount: topProfileEntry ? topProfileEntry[1] : 0,
      warning,
    };
  }, [vouchers, ownerById]);

  const recentActivities = useMemo(() => {
    const activations = vouchers
      .filter((item) => item.activated_at)
      .sort((a, b) => String(b.activated_at).localeCompare(String(a.activated_at)))
      .slice(0, 3)
      .map((item) => ({
        id: item.id || item.code,
        title: `Voucher ${item.code || '-'} diaktivasi`,
        subtitle: item.owner_name || ownerById[item.owner_id]?.name || '-',
        at: item.activated_at,
      }));
    const batchCreates = batches
      .filter((item) => item.created_at)
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
      .slice(0, 3)
      .map((item) => ({
        id: item.id || item.batch_code,
        title: `Batch ${item.batch_code || '-'} dibuat`,
        subtitle: item.owner_name || ownerById[item.owner_id]?.name || '-',
        at: item.created_at,
      }));
    const commissionChanges = commissions
      .filter((item) => item.updated_at || item.created_at)
      .sort((a, b) => String(b.updated_at || b.created_at).localeCompare(String(a.updated_at || a.created_at)))
      .slice(0, 3)
      .map((item) => ({
        id: item.id || item.voucher_id,
        title: `Komisi ${item.commission_status || item.status || 'pending'}`,
        subtitle: item.owner_name || ownerById[item.owner_id]?.name || '-',
        at: item.updated_at || item.created_at,
      }));
    return { activations, batchCreates, commissionChanges };
  }, [vouchers, batches, commissions, ownerById]);

  const analyticsDataset = useMemo(() => {
    const inRange = (dateStr) => {
      const key = String(dateStr || '').slice(0, 10);
      if (!key) return false;
      if (analyticsDateFrom && key < analyticsDateFrom) return false;
      if (analyticsDateTo && key > analyticsDateTo) return false;
      return true;
    };

    const filteredVouchers = vouchers.filter((item) => {
      const ownerOk = analyticsOwnerFilter === 'all' || String(item.owner_id || '') === analyticsOwnerFilter;
      const siteOk = analyticsSiteFilter === 'all' || String(item.site_id || '') === analyticsSiteFilter;
      const profileOk = analyticsProfileFilter === 'all'
        || String(item.profile_id || item.profile_name || '') === analyticsProfileFilter;
      const dateOk = (!analyticsDateFrom && !analyticsDateTo)
        || inRange(item.activated_at || item.created_at || item.updated_at);
      return ownerOk && siteOk && profileOk && dateOk;
    });

    const voucherByCode = Object.fromEntries(filteredVouchers.map((v) => [String(v.code || ''), v]));
    const voucherById = Object.fromEntries(filteredVouchers.map((v) => [String(v.id || ''), v]));

    const filteredCommissions = commissions.filter((item) => {
      const ownerOk = analyticsOwnerFilter === 'all' || String(item.owner_id || '') === analyticsOwnerFilter;
      const baseDate = item.activated_at || item.created_at;
      const dateOk = (!analyticsDateFrom && !analyticsDateTo) || inRange(baseDate);
      if (!ownerOk || !dateOk) return false;
      if (analyticsProfileFilter !== 'all' || analyticsSiteFilter !== 'all') {
        const refVoucher = voucherByCode[String(item.voucher_code || '')] || voucherById[String(item.voucher_id || '')];
        if (!refVoucher) return false;
        if (analyticsProfileFilter !== 'all' && String(refVoucher.profile_id || refVoucher.profile_name || '') !== analyticsProfileFilter) return false;
        if (analyticsSiteFilter !== 'all' && String(refVoucher.site_id || '') !== analyticsSiteFilter) return false;
      }
      return true;
    });

    const soldVouchers = filteredVouchers.filter((v) => ['sold', 'active'].includes(normalizeVoucherStatus(v.status)));
    const activeVouchers = filteredVouchers.filter((v) => normalizeVoucherStatus(v.status) === 'active');
    const expiredVouchers = filteredVouchers.filter((v) => normalizeVoucherStatus(v.status) === 'expired');

    const relevantLedger = financeLedgerEntries.filter((entry) => {
      const sourceType = String(entry.source_type || '').toLowerCase();
      const entryType = String(entry.entry_type || '').toLowerCase();
      const status = String(entry.status || '').toLowerCase();
      const sourceOk = sourceType.includes('voucher') || sourceType.includes('commission');
      if (!sourceOk || status === 'cancelled') return false;
      const entryDate = entry.posted_at || entry.created_at || entry.entry_date;
      if ((analyticsDateFrom || analyticsDateTo) && !inRange(entryDate)) return false;
      if (analyticsOwnerFilter !== 'all') {
        const ownerRef = String(entry.owner_id || entry.entity_id || entry.reference_owner_id || '');
        if (ownerRef && ownerRef !== analyticsOwnerFilter) return false;
      }
      if (analyticsSiteFilter !== 'all') {
        const siteRef = String(entry.site_id || entry.reference_site_id || '');
        if (siteRef && siteRef !== analyticsSiteFilter) return false;
      }
      if (analyticsProfileFilter !== 'all') {
        const profileRef = String(entry.profile_id || entry.reference_profile_id || '');
        if (profileRef && profileRef !== analyticsProfileFilter) return false;
      }
      return entryType === 'income' || entryType === 'expense';
    });

    const ledgerRevenue = relevantLedger
      .filter((entry) => String(entry.entry_type || '').toLowerCase() === 'income')
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const fallbackVoucherRevenue = soldVouchers.reduce((sum, item) => sum + Number(item.price || 0), 0);
    const totalRevenue = ledgerRevenue > 0 ? ledgerRevenue : fallbackVoucherRevenue;
    const totalCommission = filteredCommissions.reduce((sum, item) => sum + Number(item.commission_amount || item.reseller_fee || item.amount || 0), 0);

    const salesPerDayMap = {};
    const revenuePerDayMap = {};
    soldVouchers.forEach((item) => {
      const day = String(item.activated_at || item.created_at || item.updated_at || '').slice(0, 10);
      if (!day) return;
      salesPerDayMap[day] = (salesPerDayMap[day] || 0) + 1;
      revenuePerDayMap[day] = (revenuePerDayMap[day] || 0) + Number(item.price || 0);
    });
    relevantLedger
      .filter((entry) => String(entry.entry_type || '').toLowerCase() === 'income')
      .forEach((entry) => {
        const day = String(entry.posted_at || entry.created_at || entry.entry_date || '').slice(0, 10);
        if (!day) return;
        revenuePerDayMap[day] = (revenuePerDayMap[day] || 0) + Number(entry.amount || 0);
      });
    const dayKeys = Array.from(new Set([...Object.keys(salesPerDayMap), ...Object.keys(revenuePerDayMap)])).sort();
    const salesPerDay = dayKeys.slice(-30).map((day) => ({ day, sold: salesPerDayMap[day] || 0 }));
    const revenueTrend = dayKeys.slice(-30).map((day) => ({ day, revenue: revenuePerDayMap[day] || 0 }));

    const resellerMap = {};
    filteredVouchers.forEach((item) => {
      const ownerId = String(item.owner_id || '').trim() || '__none__';
      if (!resellerMap[ownerId]) resellerMap[ownerId] = { ownerId, generated: 0, sold: 0, revenue: 0, commission: 0 };
      resellerMap[ownerId].generated += 1;
      if (['sold', 'active'].includes(normalizeVoucherStatus(item.status))) {
        resellerMap[ownerId].sold += 1;
        resellerMap[ownerId].revenue += Number(item.price || 0);
      }
    });
    filteredCommissions.forEach((item) => {
      const ownerId = String(item.owner_id || '').trim() || '__none__';
      if (!resellerMap[ownerId]) resellerMap[ownerId] = { ownerId, generated: 0, sold: 0, revenue: 0, commission: 0 };
      resellerMap[ownerId].commission += Number(item.commission_amount || item.reseller_fee || item.amount || 0);
    });
    const resellerPerformance = Object.values(resellerMap).map((row) => {
      const conversion = row.generated > 0 ? (row.sold / row.generated) * 100 : 0;
      return {
        ...row,
        name: ownerById[row.ownerId]?.name || (row.ownerId === '__none__' ? 'Tanpa Owner' : row.ownerId),
        conversionRate: conversion,
      };
    }).sort((a, b) => b.revenue - a.revenue);

    const profileMap = {};
    filteredVouchers.forEach((item) => {
      const key = String(item.profile_id || item.profile_name || '').trim() || '__none__';
      if (!profileMap[key]) profileMap[key] = { key, generated: 0, active: 0, expired: 0, sold: 0 };
      profileMap[key].generated += 1;
      const status = normalizeVoucherStatus(item.status);
      if (status === 'active') profileMap[key].active += 1;
      if (status === 'expired') profileMap[key].expired += 1;
      if (status === 'sold' || status === 'active') profileMap[key].sold += 1;
    });
    const profilePerformance = Object.values(profileMap).map((row) => ({
      ...row,
      name: profileById[row.key]?.name || (row.key === '__none__' ? 'Tanpa Profile' : row.key),
      conversionRate: row.generated > 0 ? (row.sold / row.generated) * 100 : 0,
    })).sort((a, b) => b.generated - a.generated);

    const siteNasMap = {};
    filteredVouchers.forEach((item) => {
      const siteId = String(item.site_id || '').trim() || '__all_site__';
      const nasId = String(item.nas_id || '').trim() || '__all_nas__';
      const key = `${siteId}::${nasId}`;
      if (!siteNasMap[key]) siteNasMap[key] = { key, siteId, nasId, usage: 0, revenue: 0 };
      if (['sold', 'active'].includes(normalizeVoucherStatus(item.status))) {
        siteNasMap[key].usage += 1;
        siteNasMap[key].revenue += Number(item.price || 0);
      }
    });
    const siteNasPerformance = Object.values(siteNasMap).map((row) => ({
      ...row,
      siteName: siteById[row.siteId]?.name || (row.siteId === '__all_site__' ? 'All Site' : row.siteId),
      nasName: routerById[row.nasId]?.name || (row.nasId === '__all_nas__' ? 'All NAS' : row.nasId),
    })).sort((a, b) => b.revenue - a.revenue);

    const topReseller = resellerPerformance[0] || null;
    const underperformingReseller = resellerPerformance
      .filter((item) => item.generated >= 10)
      .sort((a, b) => a.conversionRate - b.conversionRate)[0] || null;
    const lowConversionProfiles = profilePerformance.filter((item) => item.generated >= 10 && item.conversionRate < 35).slice(0, 3);
    const unusedWarningCount = filteredVouchers.filter((item) => {
      if (normalizeVoucherStatus(item.status) !== 'available') return false;
      const created = new Date(item.created_at || item.updated_at || 0).getTime();
      if (!created) return false;
      const days = (Date.now() - created) / (1000 * 60 * 60 * 24);
      return days >= 7;
    }).length;

    return {
      totals: {
        sold: soldVouchers.length,
        revenue: totalRevenue,
        commission: totalCommission,
        profit: totalRevenue - totalCommission,
      },
      salesPerDay,
      revenueTrend,
      resellerPerformance,
      profilePerformance,
      siteNasPerformance,
      insights: {
        topReseller,
        underperformingReseller,
        lowConversionProfiles,
        unusedWarningCount,
      },
      counts: {
        generated: filteredVouchers.length,
        active: activeVouchers.length,
        expired: expiredVouchers.length,
      },
    };
  }, [
    vouchers,
    commissions,
    financeLedgerEntries,
    ownerById,
    profileById,
    siteById,
    routerById,
    analyticsDateFrom,
    analyticsDateTo,
    analyticsOwnerFilter,
    analyticsSiteFilter,
    analyticsProfileFilter,
  ]);

  const currentSectionMeta = SECTION_META[effectiveSection] || SECTION_META.overview;
  const visibleTabs = HOTSPOT_SECTION_TABS[effectiveSection] || [];

  const selectClass = "h-11 w-full rounded-xl border border-cyan-300/30 bg-[#081427] px-3 text-sm text-slate-100 outline-none transition-all duration-200 focus:border-cyan-300/75 focus:shadow-[0_0_0_3px_rgba(34,211,238,0.2)]";
  const selectSmClass = "h-9 w-full rounded-lg border border-cyan-300/25 bg-[#081427] px-2 text-sm text-slate-100 outline-none";

  /* Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â RENDER Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â */

  return (
    <AppShell>
    <PageContainer>
      {showPostGeneratePrintModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/70 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-cyan-300/35 bg-[#071425] shadow-2xl">
            <div className="border-b border-cyan-300/20 px-5 py-4">
              <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-300">Generate Berhasil</p>
              <h3 className="mt-1 text-lg font-semibold text-slate-100">Cetak Voucher Sekarang?</h3>
              <p className="mt-1 text-sm text-slate-400">
                Batch <span className="font-mono text-cyan-200">{lastGeneratedBatch?.batch_code || '-'}</span> siap dicetak, tanpa pindah tab manual.
              </p>
            </div>
            <div className="space-y-3 px-5 py-4">
              <div className="grid gap-2 rounded-lg border border-cyan-400/20 bg-[#061020]/60 p-3 text-xs text-slate-300 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] text-slate-400">Batch</p>
                  <p className="font-mono text-cyan-200">{lastGeneratedBatch?.batch_code || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">Quantity</p>
                  <p className="text-slate-100">{Number(lastGeneratedBatch?.quantity || selectedVoucherIds.length || 0)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">Owner</p>
                  <p className="text-slate-100">
                    {ownerById[lastGeneratedBatch?.owner_id]?.name || lastGeneratedBatch?.owner_name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">Profil</p>
                  <p className="text-slate-100">
                    {lastGeneratedBatch?.profile_name || profileById[lastGeneratedBatch?.profile_id]?.name || '-'}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[11px] text-slate-400">Scope</p>
                  <p className="text-slate-100">
                    {(lastGeneratedBatch?.site_scope || siteById[lastGeneratedBatch?.site_id]?.name || 'all')}
                    {' / '}
                    {(lastGeneratedBatch?.nas_scope || routerById[lastGeneratedBatch?.nas_id]?.name || 'all')}
                  </p>
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-400">Template Voucher</p>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => {
                    setSelectedTemplateId(e.target.value);
                    if (e.target.value) setQuickPrintTemplateError('');
                  }}
                  className={`${selectSmClass} ${quickPrintTemplateError ? 'border-rose-400/70 !bg-rose-500/10' : ''}`}
                >
                  <option value="">Pilih template voucher</option>
                  {voucherTemplates.map((tpl) => (
                    <option key={`post-generate-template-${tpl.id}`} value={String(tpl.id)}>
                      {tpl.template_name}
                    </option>
                  ))}
                </select>
                {quickPrintTemplateError ? (
                  <p className="mt-1 text-xs text-rose-300">{quickPrintTemplateError}</p>
                ) : null}
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-400">DNS Hotspot (opsional)</p>
                <Input
                  value={printDnsHost}
                  onChange={(e) => setPrintDnsHost(e.target.value)}
                  placeholder="contoh: mks.net"
                />
              </div>
              <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2 text-xs text-slate-300">
                Voucher terpilih: <span className="font-semibold text-cyan-200">{selectedVoucherIds.length}</span>
              </div>
              <div className={`rounded-lg border px-3 py-2 text-xs ${
                quickPrintReady
                  ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
                  : 'border-amber-400/30 bg-amber-500/10 text-amber-200'
              }`}>
                Status Cetak: <span className="font-semibold">{quickPrintReady ? 'Siap Cetak' : 'Belum Lengkap'}</span>
              </div>
              {!quickPrintReady && quickPrintMissingItems.length > 0 ? (
                <div className="rounded-lg border border-amber-400/30 bg-[#3a2b0a]/50 px-3 py-2 text-xs text-amber-200">
                  <p className="font-semibold">Lengkapi dulu:</p>
                  <ul className="mt-1 list-disc pl-4">
                    {quickPrintMissingItems.map((item) => (
                      <li key={`missing-${item}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {quickPrintVoucherError ? (
                <p className="text-xs text-rose-300">{quickPrintVoucherError}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-cyan-300/20 px-5 py-4">
              <KtcButton
                type="button"
                variant="secondary"
                onClick={() => {
                  setQuickPrintTemplateError('');
                  setQuickPrintVoucherError('');
                  setShowPostGeneratePrintModal(false);
                  handleTabChange('templates');
                }}
              >
                Nanti, ke Tab Cetak
              </KtcButton>
              <KtcButton type="button" onClick={handleQuickPrintFromGenerate} disabled={processingPrint || !quickPrintReady}>
                <Printer className="w-4 h-4 mr-2" />{processingPrint ? 'Memproses...' : (quickPrintReady ? 'Cetak Sekarang' : 'Lengkapi Syarat')}
              </KtcButton>
            </div>
          </div>
        </div>
      )}

      {/* Ã¢â€â‚¬Ã¢â€â‚¬ Header Ã¢â€â‚¬Ã¢â€â‚¬ */}
      <StaffPageHeader
        eyebrow={currentSectionMeta.eyebrow}
        title={currentSectionMeta.title}
        subtitle={currentSectionMeta.subtitle}
        actions={(
          <KtcButton variant="secondary" onClick={refreshAll}>
            <RefreshCw className="w-4 h-4 mr-2" />Muat Ulang
          </KtcButton>
        )}
      />

      {/* Ã¢â€â‚¬Ã¢â€â‚¬ Tab Bar Ã¢â€â‚¬Ã¢â€â‚¬ */}
      {visibleTabs.length > 0 && (
        <Card className="mt-5 mb-5 p-2">
          <div className="flex gap-1 flex-wrap">
            {visibleTabs.map((key) => {
              const meta = TAB_META[key];
              const Icon = meta?.icon || Ticket;
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabChange(key)}
                  className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-300/45 shadow-[0_0_20px_-10px_rgba(0,229,255,0.9)]'
                      : 'text-slate-300 border border-transparent hover:bg-cyan-500/10 hover:border-cyan-300/25'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {meta?.title || key}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Ã¢â€â‚¬Ã¢â€â‚¬ Summary Stats Ã¢â€â‚¬Ã¢â€â‚¬ */}
      {effectiveSection !== 'overview' && (
        <section className="mb-5 grid gap-4 sm:grid-cols-4">
          <StatCard icon={Package} label="Total Voucher" value={voucherStats.total} />
          <StatCard icon={CheckCircle} label="Tersedia" value={voucherStats.tersedia} note="siap jual" />
          <StatCard icon={CreditCard} label="Terjual" value={voucherStats.terjual} />
          <StatCard icon={Wallet} label="Revenue" value={formatCurrency(voucherStats.totalRevenue)} />
        </section>
      )}

      {/* Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â TAB: OWNERS Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â */}

      {effectiveSection === 'overview' && (
        <section className="mb-5 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Total Voucher</p><p className="text-2xl font-semibold text-slate-100">{overviewSummary.total}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Voucher Aktif</p><p className="text-2xl font-semibold text-cyan-200">{overviewSummary.active}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Voucher Kedaluwarsa</p><p className="text-2xl font-semibold text-amber-300">{overviewSummary.expired}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Revenue Voucher Hari Ini</p><p className="text-2xl font-semibold text-emerald-300">{formatCurrency(overviewSummary.todayRevenue)}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Pending Komisi Reseller</p><p className="text-2xl font-semibold text-amber-300">{formatCurrency(overviewSummary.pendingCommission)}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Total Reseller Aktif</p><p className="text-2xl font-semibold text-slate-100">{overviewSummary.activeResellers}</p></Card>
          </div>

          <Card className="p-5">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Aksi Cepat</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <KtcButton onClick={() => navigate('/dadu/hotspot/voucher?tab=generate')}>Generate Voucher</KtcButton>
              <KtcButton onClick={() => navigate('/dadu/hotspot/reseller?tab=owners')}>Tambah Reseller</KtcButton>
              <KtcButton variant="secondary" onClick={() => navigate('/dadu/hotspot/voucher?tab=vouchers')}>Lihat Daftar Voucher</KtcButton>
              <KtcButton variant="secondary" onClick={() => navigate('/dadu/hotspot/reseller?tab=commissions')}>Lihat Komisi</KtcButton>
            </div>
          </Card>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="p-5">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Insight</h2>
              {vouchers.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada data voucher untuk ditampilkan.</p>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2">
                    <p className="text-slate-400 text-xs">Reseller Teratas</p>
                    <p className="text-slate-100 font-medium">{overviewInsights.topResellerName || '-'}{overviewInsights.topResellerCount ? ` - ${overviewInsights.topResellerCount} voucher` : ''}</p>
                  </div>
                  <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2">
                    <p className="text-slate-400 text-xs">Profil Hotspot Paling Banyak Dipakai</p>
                    <p className="text-slate-100 font-medium">{overviewInsights.topProfileName || '-'}{overviewInsights.topProfileCount ? ` - ${overviewInsights.topProfileCount} penggunaan` : ''}</p>
                  </div>
                  {overviewInsights.warning ? (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-100">{overviewInsights.warning}</div>
                  ) : (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200">Kondisi konversi voucher masih sehat.</div>
                  )}
                </div>
              )}
            </Card>

            <Card className="p-5">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Aktivitas Terbaru</h2>
              {recentActivities.activations.length === 0 && recentActivities.batchCreates.length === 0 && recentActivities.commissionChanges.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada aktivitas terbaru.</p>
              ) : (
                <div className="space-y-3">
                  {recentActivities.activations.map((item) => (
                    <div key={`act-${item.id}`} className="rounded-lg border border-cyan-500/20 bg-[#081427] px-3 py-2">
                      <p className="text-sm text-slate-100">{item.title}</p>
                      <p className="text-xs text-slate-400">{item.subtitle} - {formatDateTime(item.at)}</p>
                    </div>
                  ))}
                  {recentActivities.batchCreates.map((item) => (
                    <div key={`batch-${item.id}`} className="rounded-lg border border-cyan-500/20 bg-[#081427] px-3 py-2">
                      <p className="text-sm text-slate-100">{item.title}</p>
                      <p className="text-xs text-slate-400">{item.subtitle} - {formatDateTime(item.at)}</p>
                    </div>
                  ))}
                  {recentActivities.commissionChanges.map((item) => (
                    <div key={`comm-${item.id}`} className="rounded-lg border border-cyan-500/20 bg-[#081427] px-3 py-2">
                      <p className="text-sm text-slate-100">{item.title}</p>
                      <p className="text-xs text-slate-400">{item.subtitle} - {formatDateTime(item.at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </section>
      )}

      {effectiveSection === 'analytics' && (
        <section className="mb-5 space-y-5">
          <Card className="p-5">
            <div className="grid gap-3 md:grid-cols-5">
              <div>
                <p className="text-[11px] text-slate-400 mb-1">Tanggal Mulai</p>
                <input type="date" value={analyticsDateFrom} onChange={(e) => setAnalyticsDateFrom(e.target.value)} className={selectSmClass + ' w-full'} />
              </div>
              <div>
                <p className="text-[11px] text-slate-400 mb-1">Tanggal Selesai</p>
                <input type="date" value={analyticsDateTo} onChange={(e) => setAnalyticsDateTo(e.target.value)} className={selectSmClass + ' w-full'} />
              </div>
              <div>
                <p className="text-[11px] text-slate-400 mb-1">Reseller</p>
                <select value={analyticsOwnerFilter} onChange={(e) => setAnalyticsOwnerFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                  <option value="all">Semua Reseller</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 mb-1">Site</p>
                <select value={analyticsSiteFilter} onChange={(e) => setAnalyticsSiteFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                  <option value="all">Semua Site</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 mb-1">Profile</p>
                <select value={analyticsProfileFilter} onChange={(e) => setAnalyticsProfileFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                  <option value="all">Semua Profile</option>
                  {profileFilterOptions.map((profile) => <option key={profile.value} value={profile.value}>{profile.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <KtcButton
                variant="secondary"
                onClick={() => {
                  setAnalyticsDateFrom('');
                  setAnalyticsDateTo('');
                  setAnalyticsOwnerFilter('all');
                  setAnalyticsSiteFilter('all');
                  setAnalyticsProfileFilter('all');
                }}
              >
                <Filter className="w-4 h-4 mr-2" />Reset Filter Analitik
              </KtcButton>
            </div>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Total Voucher Terjual</p><p className="text-2xl font-semibold text-slate-100">{analyticsDataset.totals.sold}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Total Pendapatan</p><p className="text-2xl font-semibold text-emerald-300">{formatCurrency(analyticsDataset.totals.revenue)}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Total Komisi</p><p className="text-2xl font-semibold text-amber-300">{formatCurrency(analyticsDataset.totals.commission)}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400 mb-1">Laba Bersih Hotspot</p><p className="text-2xl font-semibold text-cyan-200">{formatCurrency(analyticsDataset.totals.profit)}</p></Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Penjualan Voucher per Hari</h2>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart data={analyticsDataset.salesPerDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                    <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="sold" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Tren Pendapatan</h2>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <LineChart data={analyticsDataset.revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                    <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Performa Reseller</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cyan-400/20">
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Reseller</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Terjual</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Pendapatan</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Komisi</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Konversi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsDataset.resellerPerformance.slice(0, 10).map((row) => (
                      <tr key={row.ownerId} className="border-b border-cyan-400/10">
                        <td className="py-2 px-2 text-slate-100">{row.name}</td>
                        <td className="py-2 px-2 text-slate-200">{row.sold}</td>
                        <td className="py-2 px-2 text-emerald-300">{formatCurrency(row.revenue)}</td>
                        <td className="py-2 px-2 text-amber-300">{formatCurrency(row.commission)}</td>
                        <td className="py-2 px-2 text-cyan-200">{row.conversionRate.toFixed(1)}%</td>
                      </tr>
                    ))}
                    {analyticsDataset.resellerPerformance.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-6 text-slate-500">Belum ada data performa reseller</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Performa Profil</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cyan-400/20">
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Profil</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Dibuat</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Aktif</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Kedaluwarsa</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Konversi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsDataset.profilePerformance.slice(0, 10).map((row) => (
                      <tr key={row.key} className="border-b border-cyan-400/10">
                        <td className="py-2 px-2 text-slate-100">{row.name}</td>
                        <td className="py-2 px-2 text-slate-200">{row.generated}</td>
                        <td className="py-2 px-2 text-cyan-200">{row.active}</td>
                        <td className="py-2 px-2 text-amber-300">{row.expired}</td>
                        <td className="py-2 px-2 text-emerald-300">{row.conversionRate.toFixed(1)}%</td>
                      </tr>
                    ))}
                    {analyticsDataset.profilePerformance.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-6 text-slate-500">Belum ada data performa profil</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Site / NAS Performance</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cyan-400/20">
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Site</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">NAS</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Usage</th>
                      <th className="text-left py-2 px-2 text-xs text-cyan-200 uppercase">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsDataset.siteNasPerformance.slice(0, 12).map((row) => (
                      <tr key={row.key} className="border-b border-cyan-400/10">
                        <td className="py-2 px-2 text-slate-100">{row.siteName}</td>
                        <td className="py-2 px-2 text-slate-300">{row.nasName}</td>
                        <td className="py-2 px-2 text-cyan-200">{row.usage}</td>
                        <td className="py-2 px-2 text-emerald-300">{formatCurrency(row.revenue)}</td>
                      </tr>
                    ))}
                    {analyticsDataset.siteNasPerformance.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-500">Belum ada data site/NAS performance</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Smart Insights</h2>
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-3 py-2">
                  <p className="text-[11px] text-slate-400">Top Reseller</p>
                  <p className="text-slate-100">{analyticsDataset.insights.topReseller ? `${analyticsDataset.insights.topReseller.name} (${formatCurrency(analyticsDataset.insights.topReseller.revenue)})` : 'Belum ada top reseller'}</p>
                </div>
                <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2">
                  <p className="text-[11px] text-slate-400">Underperforming Reseller</p>
                  <p className="text-amber-100">{analyticsDataset.insights.underperformingReseller ? `${analyticsDataset.insights.underperformingReseller.name} (${analyticsDataset.insights.underperformingReseller.conversionRate.toFixed(1)}%)` : 'Tidak ada sinyal underperforming signifikan'}</p>
                </div>
                <div className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2">
                  <p className="text-[11px] text-slate-400">Low Conversion Profiles</p>
                  {analyticsDataset.insights.lowConversionProfiles.length ? (
                    analyticsDataset.insights.lowConversionProfiles.map((item) => (
                      <p key={item.key} className="text-rose-100">{item.name} ({item.conversionRate.toFixed(1)}%)</p>
                    ))
                  ) : (
                    <p className="text-emerald-200">Tidak ada profile dengan conversion rendah yang kritis.</p>
                  )}
                </div>
                <div className="rounded-lg border border-orange-500/25 bg-orange-500/10 px-3 py-2">
                  <p className="text-[11px] text-slate-400">Unused Voucher Warnings</p>
                  <p className="text-orange-100">{analyticsDataset.insights.unusedWarningCount} voucher available belum terpakai lebih dari 7 hari.</p>
                </div>
              </div>
              {loadingAnalytics && <p className="text-xs text-slate-500 mt-3">Sinkronisasi analytics...</p>}
            </Card>
          </div>
        </section>
      )}

      {activeTab === 'owners' && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Total Voucher</p>
              <p className="text-xl font-semibold text-slate-100">{resellerSummary.totalVoucher}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Active Voucher</p>
              <p className="text-xl font-semibold text-cyan-200">{resellerSummary.activeVoucher}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Total Komisi Payable</p>
              <p className="text-xl font-semibold text-amber-300">{formatCurrency(resellerSummary.totalPayable)}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Komisi Dibayar</p>
              <p className="text-xl font-semibold text-emerald-300">{formatCurrency(resellerSummary.totalPaid)}</p>
            </Card>
          </div>

          <div className="grid gap-5 xl:grid-cols-[400px_1fr]">
          {/* Owner Form */}
          <Card className="p-5 self-start">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4" />{ownerForm.id ? 'Edit Owner' : 'Tambah Owner / Reseller'}
            </h2>
            <form onSubmit={handleSaveOwner} className="space-y-3">
              <Input value={ownerForm.name} onChange={(e) => setOwnerForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nama Owner / Reseller" />
              <Input value={ownerForm.phone} onChange={(e) => setOwnerForm((p) => ({ ...p, phone: e.target.value }))} placeholder="No. HP (contoh: 08123456789)" />
              <Input value={ownerForm.email} onChange={(e) => setOwnerForm((p) => ({ ...p, email: e.target.value }))} placeholder="Email (opsional)" />
              <Input value={ownerForm.address} onChange={(e) => setOwnerForm((p) => ({ ...p, address: e.target.value }))} placeholder="Alamat (opsional)" />
              <div>
                <p className="text-[11px] text-slate-400 mb-1">Status</p>
                <select value={ownerForm.status} onChange={(e) => setOwnerForm((p) => ({ ...p, status: e.target.value }))} className={selectClass}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <Input value={ownerForm.notes} onChange={(e) => setOwnerForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Catatan (opsional)" />
              <div className="flex gap-2">
                <KtcButton type="submit" disabled={savingOwner}>{ownerForm.id ? 'Update Owner' : 'Tambah Owner'}</KtcButton>
                {ownerForm.id && <KtcButton type="button" variant="secondary" onClick={() => setOwnerForm(emptyOwnerForm)}>Batal</KtcButton>}
              </div>
            </form>
          </Card>

          {/* Owner List */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-4">Daftar Owner / Reseller ({filteredOwners.length})</h2>
            <SearchBar value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} placeholder="Cari nama / HP / email..." className="mb-3" />
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {loadingOwners && <p className="text-sm text-slate-400">Memuat...</p>}
              {!loadingOwners && filteredOwners.length === 0 && (
                <div className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-5 text-center">
                  <p className="text-sm text-slate-300 mb-1">Belum ada owner / reseller.</p>
                  <p className="text-xs text-slate-500 mb-3">Tambahkan owner agar voucher dan komisi dapat dikelola per reseller.</p>
                  <KtcButton type="button" onClick={() => setOwnerForm(emptyOwnerForm)}>
                    <Plus className="w-4 h-4 mr-2" />Tambah Owner
                  </KtcButton>
                </div>
              )}
              {filteredOwners.map((owner) => (
                <div key={owner.id} className="rounded-xl border border-cyan-400/25 bg-[#061020]/60 p-3 transition-all hover:border-cyan-400/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-100">{owner.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{owner.phone || '-'} | {owner.email || '-'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={owner.status === 'active' ? 'online' : 'offline'}>{owner.status || 'active'}</Badge>
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                        <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-2 py-1 text-slate-200">
                          Voucher: <span className="font-semibold">{ownerSummary[owner.id]?.vouchers || 0}</span>
                        </div>
                        <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 px-2 py-1 text-slate-200">
                          Active: <span className="font-semibold">{ownerSummary[owner.id]?.active_vouchers || 0}</span>
                        </div>
                        <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-2 py-1 text-slate-200">
                          Komisi Pending: <span className="font-semibold">{formatCurrency(ownerSummary[owner.id]?.commission_pending || 0)}</span>
                        </div>
                        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-slate-200">
                          Komisi Paid: <span className="font-semibold">{formatCurrency(ownerSummary[owner.id]?.commission_paid || 0)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <KtcButton variant="secondary" onClick={() => setOwnerForm({ ...emptyOwnerForm, ...owner })} className="text-xs px-3 py-1.5">Edit</KtcButton>
                      <KtcButton variant="danger" onClick={() => handleDeleteOwner(owner.id)} className="text-xs px-2.5 py-1.5">
                        <Trash2 className="w-3.5 h-3.5" />
                      </KtcButton>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          </div>
        </div>
      )}

      {/* Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â TAB: GENERATE Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â */}
      {activeTab === 'generate' && (
        <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4" />Generate Voucher Batch
            </h2>
            <form onSubmit={handleGenerate} className="space-y-4">
              <section className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-200 mb-3">A. Voucher Scope</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Site</p>
                    <select value={generateForm.site_id || 'all'} onChange={(e) => setGenerateForm((p) => ({ ...p, site_id: e.target.value, nas_id: 'all' }))} className={selectClass}>
                      <option value="all">Global (Semua Site)</option>
                      {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">NAS / Router</p>
                    <select value={generateForm.nas_id || 'all'} onChange={(e) => setGenerateForm((p) => ({ ...p, nas_id: e.target.value }))} className={selectClass}>
                      <option value="all">{generateForm.site_id === 'all' ? 'Global (Semua NAS)' : 'Site-wide (Semua NAS di site)'}</option>
                      {scopedRouters.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-200 mb-3">B. Owner / Hotspot Profile</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Owner / Reseller</p>
                    <select value={generateForm.owner_id || ''} onChange={(e) => setGenerateForm((p) => ({ ...p, owner_id: e.target.value, package_id: '' }))} className={selectClass}>
                      <option value="">Pilih Owner / Reseller</option>
                      {activeOwners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-[11px] text-slate-400">Hotspot Profile</p>
                      <button type="button" onClick={() => navigate('/dadu/manajemen/hotspot-profile')} className="text-[11px] text-cyan-300 hover:text-cyan-200">
                        Kelola Profile
                      </button>
                    </div>
                    <select value={generateForm.package_id || ''} onChange={(e) => setGenerateForm((p) => ({ ...p, package_id: e.target.value }))} className={selectClass}>
                      <option value="">Pilih Hotspot Profile</option>
                      {profilesByOwner.map((p) => <option key={p.id || p.name} value={p.id}>{p.name} {p.price ? `(${formatCurrency(p.price)})` : ''}</option>)}
                    </select>
                    {generateForm.owner_id && profilesByOwner.length === 0 ? (
                      <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5">
                        <p className="text-[11px] text-amber-200">Owner ini belum memiliki Hotspot Profile.</p>
                        <button
                          type="button"
                          onClick={() => navigate('/dadu/manajemen/hotspot-profile')}
                          className="whitespace-nowrap rounded border border-amber-400/40 px-2 py-0.5 text-[11px] text-amber-100 hover:bg-amber-500/20"
                        >
                          Buat Profile Hotspot
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Profile Dipilih</p>
                    <div className="h-[42px] rounded-lg border border-cyan-400/20 bg-[#071426] px-3 flex items-center text-sm text-slate-200">
                      {selectedProfile?.name || '-'}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Info Ketersediaan</p>
                    <div className="h-[42px] rounded-lg border border-cyan-400/20 bg-[#071426] px-3 flex items-center text-sm text-slate-300">
                      {generateForm.owner_id && profilesByOwner.length > 0 ? `${profilesByOwner.length} profile tersedia` : 'Pilih owner untuk melihat profile'}
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-200 mb-3">C. Login Method</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Metode Login</p>
                    <select value={generateForm.login_method} onChange={(e) => setGenerateForm((p) => ({ ...p, login_method: e.target.value }))} className={selectClass}>
                      <option value="same">Kode Tunggal (username = password)</option>
                      <option value="separate">Username + Password Terpisah</option>
                    </select>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-200 mb-3">D. Code Rules</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Prefix Kode</p>
                    <Input value={generateForm.prefix} onChange={(e) => setGenerateForm((p) => ({ ...p, prefix: e.target.value.toUpperCase() }))} placeholder="KTC" />
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Panjang Kode</p>
                    <Input type="number" value={generateForm.char_length} onChange={(e) => setGenerateForm((p) => ({ ...p, char_length: Math.min(10, Math.max(4, Number(e.target.value))) }))} />
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Charset</p>
                    <select value={generateForm.code_charset} onChange={(e) => setGenerateForm((p) => ({ ...p, code_charset: e.target.value }))} className={selectClass}>
                      <option value="alnum_upper">A-Z 0-9</option>
                      <option value="alnum_lower">a-z 0-9</option>
                      <option value="alpha">A-Z / a-z</option>
                      <option value="numeric">0-9</option>
                    </select>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-4">
                <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-200 mb-3">E. Quantity</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-400 mb-1">Jumlah Voucher</p>
                    <Input type="number" value={generateForm.quantity} onChange={(e) => setGenerateForm((p) => ({ ...p, quantity: Math.min(2000, Math.max(1, Number(e.target.value))) }))} />
                  </div>
                </div>
              </section>

              <div className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 px-4 py-3 text-xs text-slate-200">
                <p className="text-[11px] uppercase tracking-[0.12em] text-cyan-200 mb-2">Summary Preview</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <span className="text-slate-400">Owner</span><span>{ownerById[generateForm.owner_id]?.name || '-'}</span>
                  <span className="text-slate-400">Profile</span><span>{selectedProfile?.name || '-'}</span>
                  <span className="text-slate-400">Scope</span><span>{generationScope}</span>
                  <span className="text-slate-400">Login Method</span><span>{generateForm.login_method === 'same' ? 'Kode Tunggal (username = password)' : 'Username + Password Terpisah'}</span>
                  <span className="text-slate-400">Durasi Session</span><span>{formatDurationMinutes(selectedProfile?.duration_minutes)}</span>
                  <span className="text-slate-400">Masa Aktif</span><span>{formatVoucherActiveWindow(selectedProfile)}</span>
                  <span className="text-slate-400">Tipe Limit</span><span>{selectedProfile?.limit_mode || '-'}</span>
                  <span className="text-slate-400">Quantity</span><span>{Number(generateForm.quantity || 0)}</span>
                  <span className="text-slate-400">Harga per Voucher</span><span>{formatCurrency(selectedProfile?.price || 0)}</span>
                  <span className="text-slate-400">Total Nilai Stok</span><span>{formatCurrency((selectedProfile?.price || 0) * Number(generateForm.quantity || 0))}</span>
                  <span className="text-slate-400">Pratinjau Kode</span><span className="font-mono text-emerald-300">{codePreview}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <KtcButton type="submit" disabled={generating}>
                  <Zap className="w-4 h-4 mr-2" />{generating ? 'Memproses...' : `Buat ${generateForm.quantity} Voucher`}
                </KtcButton>
                <KtcButton type="button" variant="secondary" onClick={() => setGenerateForm(initialGenerateForm)}>Reset</KtcButton>
              </div>
            </form>
          </Card>

          {/* Generated Codes Preview */}
          <Card className="p-5 self-start">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Hasil Generate</h2>
              {generatedCodes.length > 0 && (
                <KtcButton variant="secondary" onClick={handleCopyAllCodes} className="text-xs px-3 py-1.5">
                  <Copy className="w-3.5 h-3.5 mr-1" />Salin Semua
                </KtcButton>
              )}
            </div>
            {lastGeneratedBatch ? (
              <div className="mb-3 rounded-lg border border-cyan-300/25 bg-cyan-500/10 px-3 py-2 text-xs text-slate-200">
                Batch terakhir: <span className="font-semibold text-cyan-100">{lastGeneratedBatch.batch_code || '-'}</span>.
                Sistem sudah menyiapkan alur cetak otomatis ke tab Template.
                <br />
                Masa aktif profil: <span className="font-semibold text-cyan-100">{formatVoucherActiveWindow(selectedProfile)}</span>.
              </div>
            ) : null}
            {generatedCodes.length === 0 ? (
              <p className="text-sm text-slate-500">Klik "Generate" untuk membuat voucher baru.</p>
            ) : (
              <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
                {generatedCodes.map((code, idx) => {
                  const codeStr = typeof code === 'string' ? code : code.code;
                  return (
                    <div key={idx} className="flex items-center justify-between rounded-lg border border-cyan-400/20 bg-[#061020]/60 px-3 py-2">
                      <span className="text-sm font-mono text-emerald-300">{codeStr}</span>
                      <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(codeStr); toast.success('Disalin'); } catch {} }} className="text-slate-500 hover:text-cyan-300 transition-colors">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â TAB: VOUCHERS Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Alur Cetak Cepat</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <KtcButton
                type="button"
                variant={templatePanelMode === 'print' ? 'primary' : 'secondary'}
                className="text-xs px-2.5 py-1.5"
                onClick={() => setTemplatePanelMode('print')}
              >
                Mode Cetak
              </KtcButton>
              <KtcButton
                type="button"
                variant={templatePanelMode === 'manage' ? 'primary' : 'secondary'}
                className="text-xs px-2.5 py-1.5"
                onClick={() => setTemplatePanelMode('manage')}
              >
                Kelola Template
              </KtcButton>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              <div className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-3">
                <p className="text-[11px] uppercase tracking-[0.1em] text-cyan-300">Step 1</p>
                <p className="mt-1 text-sm text-slate-100">Pilih Voucher</p>
                <p className="text-xs text-slate-400 mt-1">
                  {selectedVoucherIds.length > 0
                    ? `${selectedVoucherIds.length} voucher sudah dipilih`
                    : 'Belum ada voucher terpilih'}
                </p>
                <KtcButton variant="secondary" className="mt-3 text-xs px-2.5 py-1.5" onClick={() => handleTabChange('vouchers')}>
                  Pilih dari Daftar
                </KtcButton>
              </div>
              <div className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-3">
                <p className="text-[11px] uppercase tracking-[0.1em] text-cyan-300">Step 2</p>
                <p className="mt-1 text-sm text-slate-100">Pilih Template</p>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className={selectSmClass + ' mt-2 w-full'}
                >
                  <option value="">Pilih template voucher</option>
                  {voucherTemplates.map((tpl) => (
                    <option key={`print-template-${tpl.id}`} value={String(tpl.id)}>
                      {tpl.template_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  {selectedTemplateId ? `Template ID ${selectedTemplateId} dipilih` : 'Pilih template voucher di panel kiri'}
                </p>
              </div>
              <div className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-3">
                <p className="text-[11px] uppercase tracking-[0.1em] text-cyan-300">Step 3</p>
                <p className="mt-1 text-sm text-slate-100">Lanjutkan Cetak</p>
                <div className="mt-2">
                  <Input
                    value={printDnsHost}
                    onChange={(e) => setPrintDnsHost(e.target.value)}
                    placeholder="DNS hotspot (opsional)"
                  />
                </div>
                <KtcButton className="mt-3 text-xs px-2.5 py-1.5" onClick={handleContinueToPrint} disabled={processingPrint}>
                  <Printer className="w-3.5 h-3.5 mr-1.5" />{processingPrint ? 'Memproses...' : 'Lanjutkan Cetak'}
                </KtcButton>
              </div>
            </div>
          </Card>

          {templatePanelMode === 'manage' && (
          <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Template Voucher</h2>
                <KtcButton variant="secondary" onClick={fetchVoucherTemplates}>
                  <RefreshCw className="w-4 h-4 mr-2" />Muat Ulang
                </KtcButton>
              </div>
              {loadingTemplates ? (
                <p className="text-sm text-slate-400">Memuat template...</p>
              ) : (
                <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                  {voucherTemplates.length === 0 && (
                    <p className="text-sm text-slate-500">Belum ada template aktif.</p>
                  )}
                  {voucherTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setSelectedTemplateId(String(tpl.id))}
                      className={`w-full rounded-xl border px-3 py-2 text-left transition-all ${
                        String(selectedTemplateId) === String(tpl.id)
                          ? 'border-cyan-300/50 bg-cyan-500/10'
                          : 'border-cyan-400/20 bg-[#061020]/60 hover:border-cyan-300/40'
                      }`}
                    >
                      <p className="text-sm font-semibold text-slate-100">{tpl.template_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{tpl.layout_type} � {tpl.page_size}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant={tpl.is_active ? 'online' : 'offline'}>{tpl.is_active ? 'Active' : 'Inactive'}</Badge>
                        {tpl.is_default ? <Badge variant="info">Default</Badge> : null}
                        <Badge variant={tpl.builder_mode ? 'warning' : 'offline'}>
                          {tpl.builder_mode ? 'Visual Builder' : 'Advanced'}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-4">Editor Template</h2>
              <form onSubmit={handleSaveTemplate} className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <Input value={templateForm.template_name} onChange={(e) => setTemplateForm((p) => ({ ...p, template_name: e.target.value }))} placeholder="Nama template" />
                  <Input value={templateForm.template_key} onChange={(e) => setTemplateForm((p) => ({ ...p, template_key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="template_key_unik" />
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                  <select value={templateForm.category} onChange={(e) => setTemplateForm((p) => ({ ...p, category: e.target.value }))} className={selectSmClass}>
                    <option value="hotspot">hotspot</option>
                    <option value="promo">promo</option>
                    <option value="reseller">reseller</option>
                    <option value="pppoe">pppoe</option>
                  </select>
                  <select value={templateForm.layout_type} onChange={(e) => setTemplateForm((p) => ({ ...p, layout_type: e.target.value }))} className={selectSmClass}>
                    <option value="single_card">single_card</option>
                    <option value="grid_2">grid_2</option>
                    <option value="grid_3">grid_3</option>
                    <option value="grid_4">grid_4</option>
                    <option value="grid_8">grid_8</option>
                    <option value="grid_50">grid_50</option>
                    <option value="thermal">thermal</option>
                    <option value="custom">custom</option>
                  </select>
                  <select value={templateForm.page_size} onChange={(e) => setTemplateForm((p) => ({ ...p, page_size: e.target.value }))} className={selectSmClass}>
                    <option value="A4">A4</option>
                    <option value="A5">A5</option>
                    <option value="LEGAL">LEGAL (US Legal)</option>
                    <option value="thermal_58mm">thermal_58mm</option>
                    <option value="thermal_80mm">thermal_80mm</option>
                    <option value="custom">custom</option>
                  </select>
                  <Input type="number" value={templateForm.vouchers_per_page} onChange={(e) => setTemplateForm((p) => ({ ...p, vouchers_per_page: Math.max(1, Math.min(50, Number(e.target.value || 1))) }))} placeholder="vouchers/page" />
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-cyan-300/20 bg-[#061020]/70 p-2">
                  <KtcButton
                    type="button"
                    variant={templateForm.builder_mode ? 'primary' : 'secondary'}
                    onClick={() => setTemplateForm((p) => ({ ...p, builder_mode: true, builder_json: p.builder_json || DEFAULT_BUILDER_LAYOUT }))}
                  >
                    Visual Builder
                  </KtcButton>
                  <KtcButton
                    type="button"
                    variant={!templateForm.builder_mode ? 'primary' : 'secondary'}
                    onClick={() => setTemplateForm((p) => ({ ...p, builder_mode: false }))}
                  >
                    Advanced HTML/CSS
                  </KtcButton>
                </div>
                {templateForm.builder_mode ? (
                  <VoucherTemplateVisualBuilder
                    value={templateForm.builder_json || DEFAULT_BUILDER_LAYOUT}
                    onChange={(next) => setTemplateForm((p) => ({ ...p, builder_json: next }))}
                  />
                ) : (
                  <>
                    <textarea
                      value={templateForm.html_content}
                      onChange={(e) => setTemplateForm((p) => ({ ...p, html_content: e.target.value }))}
                      rows={9}
                      className="w-full rounded-xl border border-cyan-300/30 bg-[#081427] px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-300/75"
                      placeholder="HTML template voucher"
                    />
                    <textarea
                      value={templateForm.css_content}
                      onChange={(e) => setTemplateForm((p) => ({ ...p, css_content: e.target.value }))}
                      rows={5}
                      className="w-full rounded-xl border border-cyan-300/30 bg-[#081427] px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-300/75"
                      placeholder="CSS (opsional)"
                    />
                  </>
                )}
                <Input value={templateForm.notes} onChange={(e) => setTemplateForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Catatan template (opsional)" />
                <div className="flex flex-wrap gap-2">
                  <KtcButton type="submit" disabled={savingTemplate}>
                    {savingTemplate ? 'Menyimpan...' : (templateForm.id ? 'Update Template' : 'Tambah Template')}
                  </KtcButton>
                  <KtcButton type="button" variant="secondary" onClick={handleValidateTemplate}>
                    <CheckCircle className="w-4 h-4 mr-2" />Validasi
                  </KtcButton>
                  <KtcButton type="button" variant="secondary" onClick={handlePreviewTemplate}>
                    <Eye className="w-4 h-4 mr-2" />Preview
                  </KtcButton>
                </div>
                {templateValidation ? (
                  <div className={`rounded-xl border px-3 py-2 text-xs ${templateValidation.valid ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' : 'border-rose-400/30 bg-rose-500/10 text-rose-100'}`}>


                    <p>{templateValidation.message}</p>
                    {templateValidation.unknown_variables?.length ? (
                <p className="mt-1">Variabel tidak dikenali: {templateValidation.unknown_variables.join(', ')}</p>
                    ) : null}
                  </div>
                ) : null}
              </form>
            </Card>
          </div>
          )}

          <div className="grid gap-4 xl:grid-cols-[420px_1fr]">
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Cetak Voucher</h2>
                <div className="flex items-center gap-2">
                  <KtcButton variant="secondary" onClick={handleSelectFilteredVouchers}>Pilih Filter</KtcButton>
                  <KtcButton variant="secondary" onClick={handleSelectCurrentPageVouchers}>Pilih Halaman Ini</KtcButton>
                  <KtcButton variant="secondary" onClick={handleClearVoucherSelection}>Clear</KtcButton>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-3">Pilih voucher dari daftar untuk preview/cetak bulk.</p>
              <div className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
                {filteredVouchers.slice(0, 40).map((item) => {
                  const id = String(item.id || '');
                  const selected = selectedVoucherIds.includes(id);
                  return (
                    <label key={id || item.code} className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 text-xs cursor-pointer ${selected ? 'border-cyan-300/50 bg-cyan-500/10' : 'border-cyan-400/20 bg-[#061020]/60'}`}>
                      <input type="checkbox" checked={selected} onChange={() => handleToggleVoucherSelect(id)} className="mt-0.5" />
                      <span>
                        <span className="block font-mono text-emerald-300">{item.code || '-'}</span>
                        <span className="block text-slate-400">{item.profile_name || '-'} � {item.owner_name || '-'}</span>
                      </span>
                    </label>
                  );
                })}
                {filteredVouchers.length === 0 ? <p className="text-sm text-slate-500">Tidak ada voucher dari filter saat ini.</p> : null}
              </div>
              <div className="mt-3 rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2 text-xs text-slate-300">
                Terpilih: <span className="font-semibold text-cyan-200">{selectedVoucherIds.length}</span> voucher
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <KtcButton type="button" onClick={handleContinueToPrint} disabled={processingPrint}>
                  <Printer className="w-4 h-4 mr-2" />{processingPrint ? 'Memproses...' : 'Lanjutkan Cetak'}
                </KtcButton>
                <KtcButton type="button" variant="secondary" onClick={handleExportPrintPdf}>
                  <Download className="w-4 h-4 mr-2" />Ekspor PDF
                </KtcButton>
                <KtcButton type="button" variant="secondary" onClick={handleMarkPrinted}>
                  <CheckCircle className="w-4 h-4 mr-2" />Tandai Sudah Dicetak
                </KtcButton>
              </div>
              {lastPrintJob ? (
                <div className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
                  Print Job #{lastPrintJob.job_id || lastPrintJob.id} � Status: {lastPrintJob.status || '-'}
                </div>
              ) : null}
            </Card>

            <Card className="p-5">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-3">Preview Template</h2>
              <div className="mb-3 text-xs text-slate-400">
                {templatePreviewMeta ? (
                  <span>
                    Template #{templatePreviewMeta.template_id} � Voucher {templatePreviewMeta.voucher_count || 0} � Halaman {templatePreviewMeta.page_count || 0} � {templatePreviewMeta.layout_type || '-'} � {templatePreviewMeta.page_size || '-'} {templatePreviewMeta.orientation ? `(${templatePreviewMeta.orientation})` : ''}
                  </span>
                ) : (
                  <span>Belum ada preview. Pilih template lalu klik Preview.</span>
                )}
              </div>
              {previewError ? (
                <div className="mb-3 rounded-md border border-rose-500/35 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                  {previewError}
                </div>
              ) : null}
              <div className="rounded-xl border border-cyan-400/20 bg-white min-h-[320px] overflow-hidden">
                {previewLoading ? (
                  <div className="h-[320px] flex items-center justify-center text-sm text-slate-500">Menyiapkan preview...</div>
                ) : templatePreview ? (
                  <iframe
                    title="voucher-template-preview"
                    className="w-full h-[520px]"
                    srcDoc={templatePreview}
                  />
                ) : (
                  <div className="h-[320px] flex items-center justify-center text-sm text-slate-500">Preview akan tampil setelah template dipilih.</div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}
      {activeTab === 'vouchers' && (
        <Card className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Daftar Voucher ({filteredVouchers.length})</h2>
            <div className="flex items-center gap-2">
              <KtcButton
                variant="secondary"
                onClick={() => {
                  setVoucherSearch('');
                  setVoucherStatusFilter('all');
                  setVoucherOwnerFilter('all');
                  setVoucherProfileFilter('all');
                  setVoucherSiteFilter('all');
                  setVoucherNasFilter('all');
                  setVoucherBatchFilter('all');
                  setVoucherPage(1);
                }}
              >
                <Filter className="w-4 h-4 mr-2" />Reset Filter
              </KtcButton>
            </div>
          </div>

          <div className="rounded-xl border border-cyan-400/20 bg-[#061020]/60 p-3 mb-4">
            <div className="grid gap-3 lg:grid-cols-4">
              <SearchBar value={voucherSearch} onChange={(e) => setVoucherSearch(e.target.value)} placeholder="Cari berdasarkan kode voucher..." />
              <select value={voucherStatusFilter} onChange={(e) => setVoucherStatusFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                <option value="all">Semua Status</option>
                {[
                  { key: 'available', label: 'Tersedia' },
                  { key: 'sold', label: 'Terjual' },
                  { key: 'active', label: 'Aktif' },
                  { key: 'used', label: 'Dipakai/Habis' },
                  { key: 'expired', label: 'Kedaluwarsa' },
                  { key: 'deleted', label: 'Dihapus' },
                  { key: 'cancelled', label: 'Dibatalkan' },
                ].map((status) => (
                  <option key={status.key} value={status.key}>{status.label}</option>
                ))}
              </select>
              <select value={voucherOwnerFilter} onChange={(e) => setVoucherOwnerFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                <option value="all">Semua Owner</option>
                {owners.map((owner) => <option key={owner.id} value={owner.id}>{owner.name}</option>)}
              </select>
              <select value={voucherProfileFilter} onChange={(e) => setVoucherProfileFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                <option value="all">Semua Profile</option>
                {profileFilterOptions.map((profile) => <option key={profile.value} value={profile.value}>{profile.label}</option>)}
              </select>
            </div>
            <div className="grid gap-3 lg:grid-cols-4 mt-3">
              <select value={voucherSiteFilter} onChange={(e) => { setVoucherSiteFilter(e.target.value); setVoucherNasFilter('all'); }} className={selectSmClass + ' w-full'}>
                <option value="all">Semua Site</option>
                {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
              </select>
              <select value={voucherNasFilter} onChange={(e) => setVoucherNasFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                <option value="all">Semua NAS</option>
                {routers.filter((router) => voucherSiteFilter === 'all' || String(router.site_id || '') === voucherSiteFilter).map((router) => (
                  <option key={router.id} value={router.id}>{router.name}</option>
                ))}
              </select>
              <select value={voucherBatchFilter} onChange={(e) => setVoucherBatchFilter(e.target.value)} className={selectSmClass + ' w-full'}>
                <option value="all">Semua Batch</option>
                {batches.map((batch) => <option key={batch.id || batch.batch_code} value={batch.batch_code}>{batch.batch_code}</option>)}
              </select>
              <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-3 py-2 text-xs text-slate-300">
                Menampilkan <span className="font-semibold text-cyan-200">{paginatedVouchers.length}</span> dari <span className="font-semibold text-cyan-200">{filteredVouchers.length}</span> voucher
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <KtcButton variant="secondary" className="text-xs px-2.5 py-1.5" onClick={handleSelectFilteredVouchers}>Pilih Hasil Filter</KtcButton>
              <KtcButton variant="secondary" className="text-xs px-2.5 py-1.5" onClick={handleSelectCurrentPageVouchers}>Pilih Halaman Ini</KtcButton>
              <KtcButton variant="secondary" className="text-xs px-2.5 py-1.5" onClick={handleSelectCurrentBatchForPrint}>Pilih per Batch</KtcButton>
              <KtcButton variant="secondary" className="text-xs px-2.5 py-1.5" onClick={handleClearVoucherSelection}>Clear Pilihan</KtcButton>
              <KtcButton className="text-xs px-2.5 py-1.5" onClick={handleMoveToPrintFlow}>
                <Printer className="w-3.5 h-3.5 mr-1.5" />Cetak Terpilih
              </KtcButton>
              <KtcButton variant="secondary" className="text-xs px-2.5 py-1.5" onClick={handleBulkDisableVouchers}>Disable Terpilih</KtcButton>
              <KtcButton variant="danger" className="text-xs px-2.5 py-1.5" onClick={handleBulkDeleteVouchers}>Hapus Terpilih</KtcButton>
              <span className="text-xs text-slate-300">Terpilih: <span className="font-semibold text-cyan-200">{selectedVoucherIds.length}</span></span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cyan-400/20">
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">#</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Username</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Password</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Batch</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Profil</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Harga</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Owner</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Scope</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Status</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Sisa Session</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Masa Aktif Profil</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Mulai Aktif</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Berakhir</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Sumber</th>
                  <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loadingVouchers && (
                  <tr><td colSpan={15} className="text-center py-8 text-slate-400">Memuat voucher...</td></tr>
                )}
                {!loadingVouchers && filteredVouchers.length === 0 && (
                  <tr><td colSpan={15} className="text-center py-8 text-slate-500">Tidak ada voucher ditemukan</td></tr>
                )}
                {paginatedVouchers.map((v) => {
                  const runtimeKey = resolveVoucherRuntimeStatus(v);
                  const statusMeta = VOUCHER_STATUS_MAP[runtimeKey] || VOUCHER_STATUS_MAP[String(v.status || '').toLowerCase()] || VOUCHER_STATUS_MAP.tersedia;
                  const scopeText = v.scope_label || v.voucher_scope || `${siteById[v.site_id]?.name || (v.site_id ? v.site_id : 'All Site')} / ${routerById[v.nas_id]?.name || (v.nas_id ? v.nas_id : 'All NAS')}`;
                  const usernameText = v.username || v.user || v.user_name || v.voucher_username || v.login_username || v.code || '-';
                  const passwordText = v.password || v.pass || v.user_password || v.voucher_password || v.login_password || v.code || '-';
                  const voucherId = String(v.id || '').trim();
                  const selected = voucherId ? selectedVoucherIds.includes(voucherId) : false;
                  return (
                    <tr key={v.id || v.code} className="border-b border-cyan-400/10 hover:bg-cyan-500/5 transition-colors">
                      <td className="py-2.5 px-3 text-slate-300">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => handleToggleVoucherSelect(voucherId)}
                          disabled={!voucherId}
                        />
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-200 text-xs">{usernameText}</td>
                      <td className="py-2.5 px-3 font-mono text-slate-200 text-xs">{passwordText}</td>
                      <td className="py-2.5 px-3 text-slate-300">{v.batch_code || '-'}</td>
                      <td className="py-2.5 px-3 text-slate-200">{v.profile_name || '-'}</td>
                      <td className="py-2.5 px-3 text-slate-200">{formatCurrency(v.price)}</td>
                      <td className="py-2.5 px-3 text-slate-300">{v.owner_name || ownerById[v.owner_id]?.name || '-'}</td>
                      <td className="py-2.5 px-3 text-slate-400 text-xs">{scopeText}</td>
                      <td className="py-2.5 px-3"><Badge variant={statusMeta.variant}>{statusMeta.label}</Badge></td>
                      <td className="py-2.5 px-3 text-slate-300">{runtimeKey === 'active' ? formatSessionRemaining(v) : '-'}</td>
                      <td className="py-2.5 px-3 text-slate-300">{formatVoucherActiveWindow(v)}</td>
                      <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDateTime(v.activated_at)}</td>
                      <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDateTime(v.effective_expires_at || v.expires_at || v.fixed_expires_at)}</td>
                      <td className="py-2.5 px-3 text-slate-300 text-xs">{String(v.masa_aktif_sumber || '-')}</td>
                      <td className="py-2.5 px-3 text-slate-300 text-xs">
                        <KtcButton
                          variant="secondary"
                          className="text-[11px] px-2 py-1"
                          onClick={() => handleDisconnectVoucherSession(v)}
                          disabled={!voucherId}
                        >
                          Disconnect Session
                        </KtcButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-300">
            <div>
              Halaman <span className="font-semibold text-cyan-200">{safeVoucherPage}</span> / <span className="font-semibold text-cyan-200">{voucherTotalPages}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>Per halaman</span>
              <select
                value={voucherPageSize}
                onChange={(e) => setVoucherPageSize(Math.max(10, Math.min(100, Number(e.target.value || 25))))}
                className={selectSmClass}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <KtcButton
                variant="secondary"
                className="text-xs px-2.5 py-1.5"
                onClick={() => setVoucherPage((prev) => Math.max(1, Number(prev || 1) - 1))}
                disabled={safeVoucherPage <= 1}
              >
                Sebelumnya
              </KtcButton>
              <KtcButton
                variant="secondary"
                className="text-xs px-2.5 py-1.5"
                onClick={() => setVoucherPage((prev) => Math.min(voucherTotalPages, Number(prev || 1) + 1))}
                disabled={safeVoucherPage >= voucherTotalPages}
              >
                Berikutnya
              </KtcButton>
            </div>
          </div>
        </Card>
      )}

      {/* Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â TAB: COMMISSIONS Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â */}
      
      {activeTab === 'batches' && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Batch Voucher ({batches.length})</h2>
              <KtcButton variant="secondary" onClick={() => navigate('/dadu/hotspot/voucher?tab=generate')}>
                <Plus className="w-4 h-4 mr-2" />Generate Batch Baru
              </KtcButton>
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-3">
                <p className="text-[11px] text-slate-400">Total Batch</p>
                <p className="text-lg font-semibold text-slate-100">{batches.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
                <p className="text-[11px] text-slate-400">Voucher Active</p>
                <p className="text-lg font-semibold text-emerald-300">{Object.values(batchStatsByCode).reduce((sum, item) => sum + Number(item.active || 0), 0)}</p>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                <p className="text-[11px] text-slate-400">Voucher Expired</p>
                <p className="text-lg font-semibold text-amber-300">{Object.values(batchStatsByCode).reduce((sum, item) => sum + Number(item.expired || 0), 0)}</p>
              </div>
              <div className="rounded-xl border border-blue-500/20 bg-blue-500/10 p-3">
                <p className="text-[11px] text-slate-400">Voucher Available</p>
                <p className="text-lg font-semibold text-blue-300">{Object.values(batchStatsByCode).reduce((sum, item) => sum + Number(item.available || 0), 0)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cyan-400/20">
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Batch Code</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Owner</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Profile</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Generated</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Active</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Expired</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Scope</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Created At</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-slate-500">Belum ada batch voucher</td></tr>
                  ) : (
                    batches.map((batch, index) => {
                      const stats = batchStatsByCode[String(batch.batch_code || '')] || {};
                      const generatedCount = Number(stats.generated || batch.quantity || 0);
                      return (
                        <tr key={batch.id || batch.batch_code || index} className="border-b border-cyan-400/10 hover:bg-cyan-500/5 transition-colors">
                          <td className="py-2.5 px-3 font-mono text-emerald-300 text-xs">{batch.batch_code || '-'}</td>
                          <td className="py-2.5 px-3 text-slate-200">{batch.owner_name || ownerById[batch.owner_id]?.name || '-'}</td>
                          <td className="py-2.5 px-3 text-slate-300">{batch.profile_name || profileById[batch.profile_id]?.name || '-'}</td>
                          <td className="py-2.5 px-3 text-slate-200">{generatedCount}</td>
                          <td className="py-2.5 px-3"><Badge variant="info">{Number(stats.active || 0)}</Badge></td>
                          <td className="py-2.5 px-3"><Badge variant="warning">{Number(stats.expired || 0)}</Badge></td>
                          <td className="py-2.5 px-3 text-slate-400 text-xs">
                            {(batch.site_scope || siteById[batch.site_id]?.name || 'all')} / {(batch.nas_scope || routerById[batch.nas_id]?.name || 'all')}
                          </td>
                          <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDate(batch.created_at)}</td>
                          <td className="py-2.5 px-3">
                            <KtcButton variant="secondary" className="text-xs px-2.5 py-1.5" onClick={() => handleOpenBatchDetail(batch.batch_code)}>
                              Lihat Voucher
                            </KtcButton>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
      {activeTab === 'commissions' && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Total Payable</p>
              <p className="text-xl font-bold text-amber-400">{formatCurrency(filteredCommissions.filter((c) => !isCommissionPaid(c)).reduce((s, c) => s + Number(c.reseller_fee || c.amount || c.commission_amount || 0), 0))}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Total Paid</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(filteredCommissions.filter((c) => isCommissionPaid(c)).reduce((s, c) => s + Number(c.reseller_fee || c.amount || c.commission_amount || 0), 0))}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-slate-400 mb-1">Pending Payout Count</p>
              <p className="text-xl font-bold text-slate-100">{filteredCommissions.filter((c) => !isCommissionPaid(c)).length}</p>
            </Card>
          </div>

          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em]">Komisi Reseller</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={commissionOwnerFilter} onChange={(e) => setCommissionOwnerFilter(e.target.value)} className={selectSmClass + ' w-44'}>
                  <option value="all">Semua Owner</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <select value={commissionStatusFilter} onChange={(e) => setCommissionStatusFilter(e.target.value)} className={selectSmClass + ' w-36'}>
                  <option value="all">Semua Status</option>
                  <option value="pending">Pending</option>
                  <option value="payable">Payable</option>
                  <option value="paid">Paid</option>
                </select>
                <input type="date" value={commissionDateFrom} onChange={(e) => setCommissionDateFrom(e.target.value)} className={selectSmClass + ' w-40'} />
                <input type="date" value={commissionDateTo} onChange={(e) => setCommissionDateTo(e.target.value)} className={selectSmClass + ' w-40'} />
                <KtcButton
                  variant="secondary"
                  onClick={() => {
                    setCommissionOwnerFilter('all');
                    setCommissionStatusFilter('all');
                    setCommissionDateFrom('');
                    setCommissionDateTo('');
                  }}
                >
                  Reset
                </KtcButton>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cyan-400/20">
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Reseller</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Referensi</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Activation Date</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Commission Amount</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Commission Status</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Paid At</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCommissions.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500">Belum ada data komisi untuk filter ini</td></tr>
                  ) : (
                    filteredCommissions.map((c, idx) => (
                      <tr key={c.id || idx} className="border-b border-cyan-400/10 hover:bg-cyan-500/5 transition-colors">
                        <td className="py-2.5 px-3 text-slate-200">{c.owner_name || ownerById[c.owner_id]?.name || '-'}</td>
                        <td className="py-2.5 px-3 font-mono text-emerald-300 text-xs">{c.voucher_code || c.batch_code || c.voucher_id || '-'}</td>
                        <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDate(c.activated_at || c.created_at)}</td>
                        <td className="py-2.5 px-3 text-cyan-300 font-semibold">{formatCurrency(c.reseller_fee || c.amount || c.commission_amount || 0)}</td>
                        <td className="py-2.5 px-3"><Badge variant={isCommissionPaid(c) ? 'online' : 'warning'}>{c.commission_status || c.status || 'pending'}</Badge></td>
                        <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDate(c.paid_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'topup' && (
        <div className="space-y-4">
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-cyan-200 uppercase tracking-[0.12em] mb-4">Topup Reseller</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-3">
                <p className="text-[11px] text-slate-400">Total Reseller</p>
                <p className="text-lg font-semibold text-slate-100">{owners.length}</p>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                <p className="text-[11px] text-slate-400">Pending Komisi</p>
                <p className="text-lg font-semibold text-amber-300">{formatCurrency(resellerSummary.totalPayable)}</p>
              </div>
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
                <p className="text-[11px] text-slate-400">Komisi Dibayar</p>
                <p className="text-lg font-semibold text-emerald-300">{formatCurrency(resellerSummary.totalPaid)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-100 mb-2">Riwayat Topup Terbaru</h3>
            <p className="text-sm text-slate-400">Riwayat topup reseller akan tampil otomatis saat transaksi topup sudah tersedia.</p>
            <div className="mt-4">
              <KtcButton variant="secondary" onClick={() => handleTabChange('owners')}>
                Kembali ke Owner / Reseller
              </KtcButton>
            </div>
          </Card>
        </div>
      )}
    </PageContainer>
    </AppShell>
  );
};

export default AdminVoucherSystem;








